// ============================================================
// motor_control.js
// EV Powertrain Simulator — core 모듈
// 모터 제어 — Bode, Nyquist, 근궤적, PID 자동 튜닝, Gain Scheduling
// Extracted from ev-sim_v1_0_Verification_46.jsx (Phase 2)
// ============================================================


// ── DRIVE_MODE_GAIN_TABLE — SST: ./drive_mode_config.js ──────────────────
// 게인 배율 수정은 drive_mode_config.js 1곳에서만 할 것.
import { DRIVE_MODE_GAIN_TABLE } from './drive_mode_config.js';
import { G_GRAVITY } from '../../constants/physics_consts.js';

/**
 * applyDriveModeGains(params)
 *
 * drive_mode ('ECO' | 'NORMAL' | 'SPORT') 에 따라
 * FOC 전류 제어기(Kp_id, Ki_id, Kp_iq, Ki_iq)와
 * 속도 제어기(Kp_spd, Ki_spd)를 결정하여 새 params 객체를 반환.
 *
 * 우선 순위 (내림차순):
 *  1. params.mode_gains[mode]  — 사용자가 모드별로 직접 설정한 절대값
 *  2. DRIVE_MODE_GAIN_TABLE 배율 × NORMAL 기준값  — 자동 계산 폴백
 *
 * - 원본 params 객체는 변경하지 않음 (immutable)
 * - 알 수 없는 모드는 NORMAL로 폴백
 * - 디버그 메타 _dm_* 네임스페이스로 보존
 *
 * @param {object} params - 차량/모터 파라미터
 * @returns {object} 드라이브 모드 적용된 파라미터 사본
 */
function applyDriveModeGains(params) {
  const mode = (params.drive_mode || 'NORMAL').toUpperCase();
  const factors = DRIVE_MODE_GAIN_TABLE[mode] || DRIVE_MODE_GAIN_TABLE.NORMAL;

  // NORMAL 기준값 (모든 모드 계산의 베이스라인)
  const base_Kp_id  = params.Kp_id  ?? 1.51;
  const base_Ki_id  = params.Ki_id  ?? 75;
  const base_Kp_iq  = params.Kp_iq  ?? 3.02;
  const base_Ki_iq  = params.Ki_iq  ?? 75;
  const base_Kp_spd = params.Kp_spd ?? 143.4;
  const base_Ki_spd = params.Ki_spd ?? 3003;

  // ① 사용자가 해당 모드의 게인을 직접 설정했는지 확인
  const customGains = params.mode_gains?.[mode];
  const hasCustom = !!customGains;

  // ② 유효 게인 결정: 커스텀 > 자동 배율
  const eff_Kp_id  = hasCustom && customGains.Kp_id  != null
    ? customGains.Kp_id
    : +(base_Kp_id  * factors.Kp_id ).toFixed(4);
  const eff_Ki_id  = hasCustom && customGains.Ki_id  != null
    ? customGains.Ki_id
    : +(base_Ki_id  * factors.Ki_id ).toFixed(3);
  const eff_Kp_iq  = hasCustom && customGains.Kp_iq  != null
    ? customGains.Kp_iq
    : +(base_Kp_iq  * factors.Kp_iq ).toFixed(4);
  const eff_Ki_iq  = hasCustom && customGains.Ki_iq  != null
    ? customGains.Ki_iq
    : +(base_Ki_iq  * factors.Ki_iq ).toFixed(3);
  const eff_Kp_spd = hasCustom && customGains.Kp_spd != null
    ? customGains.Kp_spd
    : +(base_Kp_spd * factors.Kp_spd).toFixed(3);
  const eff_Ki_spd = hasCustom && customGains.Ki_spd != null
    ? customGains.Ki_spd
    : +(base_Ki_spd * factors.Ki_spd).toFixed(2);

  return {
    ...params,
    // ── 드라이브 모드 유효 게인 ─────────────────────────────────
    Kp_id:  eff_Kp_id,
    Ki_id:  eff_Ki_id,
    Kp_iq:  eff_Kp_iq,
    Ki_iq:  eff_Ki_iq,
    Kp_spd: eff_Kp_spd,
    Ki_spd: eff_Ki_spd,
    // ── 디버깅·UI용 메타 ────────────────────────────────────────
    _dm_mode:        mode,
    _dm_factors:     factors,
    _dm_has_custom:  hasCustom,
    _dm_base_Kp_id:  base_Kp_id,
    _dm_base_Ki_id:  base_Ki_id,
    _dm_base_Kp_iq:  base_Kp_iq,
    _dm_base_Ki_iq:  base_Ki_iq,
    _dm_base_Kp_spd: base_Kp_spd,
    _dm_base_Ki_spd: base_Ki_spd,
  };
}

/**
 * getModeGainsForUI(params, mode)
 *
 * UI 표시용: 특정 모드의 현재 게인을 반환.
 * mode_gains 커스텀값 우선, 없으면 배율 계산값.
 */
/**
 * UI 표시용 특정 모드의 현재 게인을 반환한다.
 *
 * applyDriveModeGains와 동일한 우선순위(커스텀 > 배율 자동 계산)를 적용하되,
 * params 자체를 변경하지 않고 표시용 객체만 반환한다.
 * UI 게인 인스펙터 패널에서 모드별 탭 전환 시 호출된다.
 *
 * @param {object} params  차량/모터 파라미터
 * @param {string} mode    드라이브 모드 ('ECO' | 'NORMAL' | 'SPORT' | 'SNOW' | 'OFF_ROAD')
 * @returns {{ Kp_id, Ki_id, Kp_iq, Ki_iq, Kp_spd, Ki_spd, isCustom: boolean }}
 */
function getModeGainsForUI(params, mode) {
  const m = (mode || 'NORMAL').toUpperCase();
  const f = DRIVE_MODE_GAIN_TABLE[m] || DRIVE_MODE_GAIN_TABLE.NORMAL;
  const custom = params.mode_gains?.[m];
  const base_Kp_id  = params.Kp_id  ?? 1.51;
  const base_Ki_id  = params.Ki_id  ?? 75;
  const base_Kp_iq  = params.Kp_iq  ?? 3.02;
  const base_Ki_iq  = params.Ki_iq  ?? 75;
  const base_Kp_spd = params.Kp_spd ?? 143.4;
  const base_Ki_spd = params.Ki_spd ?? 3003;
  return {
    Kp_id:  custom?.Kp_id  ?? +(base_Kp_id  * f.Kp_id ).toFixed(4),
    Ki_id:  custom?.Ki_id  ?? +(base_Ki_id  * f.Ki_id ).toFixed(3),
    Kp_iq:  custom?.Kp_iq  ?? +(base_Kp_iq  * f.Kp_iq ).toFixed(4),
    Ki_iq:  custom?.Ki_iq  ?? +(base_Ki_iq  * f.Ki_iq ).toFixed(3),
    Kp_spd: custom?.Kp_spd ?? +(base_Kp_spd * f.Kp_spd).toFixed(3),
    Ki_spd: custom?.Ki_spd ?? +(base_Ki_spd * f.Ki_spd).toFixed(2),
    isCustom: !!custom,
  };
}

// ── computeCurrentLoopBode ──
// [Code-Quality-1] 단일축(d축) Bode — computeCurrentLoopBode_dq의 래퍼로 리팩터링.
//   기존: dq 버전과 동일한 100줄 로직을 중복 구현 (유지보수 분산, Kp_id/Kq 혼용 위험).
//   수정: dq 버전으로 d축만 계산 후 단일 반환 형태로 재구성.
//   외부 시그니처(반환 필드)는 기존과 동일하게 유지 → 호출 코드 무변경.
/**
 * 전류 루프(d축) 개루프 Bode 선도를 계산한다.
 *
 * computeCurrentLoopBode_dq의 d축 전용 래퍼이다.
 * 플랜트 G(s) = 1/(Rs + Ld·s), 제어기 C(s) = Kp_id + Ki_id/s,
 * 연산 지연 D(s) = e^(-1.5·dt·s) 를 포함한 개루프 L(s) = C·G·D 의
 * 주파수 응답 데이터와 위상 여유(PM), 이득 여유(GM), 대역폭(BW)을 반환한다.
 *
 * @param {object} params  차량/모터 파라미터 (Kp_id, Ki_id, Ld, Rs, ctrl_dt 포함)
 * @returns {{ data, pm, gm, bw, bw_theory, zero_freq, f_range, stable, Kp, Ki, L, R, dt }}
 */
function computeCurrentLoopBode(params) {
  const [d] = computeCurrentLoopBode_dq(params);  // d축 결과만 사용
  return {
    data:       d.data,
    pm:         d.pm,
    gm:         d.gm,
    bw:         d.bw,
    bw_theory:  d.bw_theory,
    zero_freq:  d.zero_freq,
    f_range:    d.f_range,
    stable:     d.stable,
    Kp:         d.Kp,
    Ki:         d.Ki,
    L:          d.L,
    R:          d.R,
    dt:         d.dt,
  };
}


// ── computeCurrentLoopBode_dq ──
// d축(Ld, Kp_id, Ki_id)과 q축(Lq, Kp_iq, Ki_iq)을 각각 계산
/**
 * FOC 전류 루프 d·q 두 축의 Bode 선도를 동시에 계산한다.
 *
 * 각 축에 대해 개루프 전달함수 L(jω) = C(jω)·G(jω)·D(jω) 의
 * 복소 크기·위상을 300점 로그 스케일 주파수 스윕으로 계산한다.
 * 폐루프 전달함수 T(jω) = L/(1+L) 의 -3dB 주파수를 대역폭(BW)으로 정의하며,
 * 개루프가 0dB를 교차하는 주파수에서의 위상 여유(PM)와
 * 위상이 -180°를 교차하는 주파수에서의 이득 여유(GM)를 동시에 산출한다.
 *
 * @param {object} params  Ld, Lq, Kp_id, Ki_id, Kp_iq, Ki_iq, Rs, ctrl_dt 포함
 * @returns {Array<object>}  길이 2의 배열 [d축 결과, q축 결과]
 */
function computeCurrentLoopBode_dq(params) {
  const axes = [
    { label: 'd', L: params.Ld||0.0003, Kp: params.Kp_id||1.51,
      Ki: params.Ki_id||75, R: params.Rs||0.015, dt: params.ctrl_dt||1e-4 },
    { label: 'q', L: params.Lq||0.0006, Kp: params.Kp_iq||3.02,
      Ki: params.Ki_iq||75, R: params.Rs||0.015, dt: params.ctrl_dt||1e-4 },
  ];
  return axes.map(ax => {
    const { L, Kp, Ki, R, dt } = ax;
    const T_delay = 1.5 * dt;
    const wc_est  = Kp / L;
    const f_c_est = wc_est / (2 * Math.PI);
    const f_min   = Math.max(1, f_c_est / 500);
    const f_max   = Math.min(5e5, f_c_est * 10);
    const N_pts   = 300;
    const data    = [];
    let pm = null, gm = null, bw3dB = null;
    let prevMag = null, prevPhase = null;
    for (let i = 0; i < N_pts; i++) {
      const f = f_min * Math.pow(f_max / f_min, i / (N_pts - 1));
      const w = 2 * Math.PI * f;
      const denom2 = R*R + w*w*L*L;
      const G_re = R / denom2, G_im = -w * L / denom2;
      const C_re = Kp, C_im = -Ki / w;
      const D_re = Math.cos(w * T_delay), D_im = -Math.sin(w * T_delay);
      const CG_re = C_re*G_re - C_im*G_im, CG_im = C_re*G_im + C_im*G_re;
      const L_re  = CG_re*D_re - CG_im*D_im, L_im = CG_re*D_im + CG_im*D_re;
      const mag = Math.sqrt(L_re*L_re + L_im*L_im);
      const mag_dB = 20 * Math.log10(Math.max(mag, 1e-12));
      const phase_deg = Math.atan2(L_im, L_re) * 180 / Math.PI;
      const one_pL_re = 1 + L_re;
      const dT2 = one_pL_re*one_pL_re + L_im*L_im;
      const T_re = (L_re*one_pL_re + L_im*L_im) / dT2;
      const T_im = L_im / dT2;
      const T_dB = 20 * Math.log10(Math.max(Math.sqrt(T_re*T_re + T_im*T_im), 1e-12));
      if (bw3dB === null && T_dB < -3 && i > 0) bw3dB = f;
      if (prevMag !== null && prevMag >= 1 && mag < 1) {
        const alpha = (prevMag - 1) / (prevMag - mag);
        pm = 180 + prevPhase + (phase_deg - prevPhase) * alpha;
      }
      if (prevPhase !== null && prevPhase > -180 && phase_deg < -180) {
        const alpha = (prevPhase + 180) / (prevPhase - phase_deg);
        const magInt = prevMag + (mag - prevMag) * alpha;
        gm = -20 * Math.log10(Math.max(magInt, 1e-12));
      }
      data.push({ freq_hz: +f.toFixed(2), mag_dB: +mag_dB.toFixed(2),
                  phase_deg: +phase_deg.toFixed(2), T_dB: +T_dB.toFixed(2) });
      prevMag = mag; prevPhase = phase_deg;
    }
    return {
      axis: ax.label, data,
      pm: pm !== null ? +pm.toFixed(1) : null,
      gm: gm !== null ? +gm.toFixed(1) : null,
      bw: bw3dB !== null ? +bw3dB.toFixed(0) : null,
      bw_theory: +(f_c_est.toFixed(0)),
      zero_freq: +(Ki / Kp / (2*Math.PI)).toFixed(1),
      f_range: [+f_min.toFixed(2), +f_max.toFixed(0)],
      stable: (pm !== null && pm > 0) && (gm === null || gm > 0),
      Kp, Ki, L, R, dt,
    };
  });
}

// ── computeSpeedLoopBode ──
/**
 * 속도 루프 개루프 Bode 선도를 계산한다.
 *
 * 전류 루프를 1차 저역통과 근사(Gi = ωci/(s+ωci))로 단순화하고,
 * 기계 플랜트 Gm = 1/(J_eq·s) 와 결합하여 전체 플랜트 P = Gi·Gm 를 구성한다.
 * 속도 PI 제어기 C(s) = Kp + Ki/s 를 포함한 개루프 L = C·P 의
 * Bode 선도를 300점 로그 스케일 주파수 스윕으로 계산한다.
 * 이론 교차주파수 fc = Kp/(2π·J_eq), PI 영점 주파수 fz = Ki/Kp/2π 를 함께 반환한다.
 *
 * @param {object} params  Kp_spd, Ki_spd, J_motor, gear_ratio, m, r_wheel, Kp_id, Ld 포함
 * @returns {{ data, pm, gm, gm_inf, bw, bw_theory, zero_freq, stable, Kp, Ki, J_eq, wc_inner }}
 */
function computeSpeedLoopBode(params) {
  const Kp   = params.Kp_spd  || 2.0;
  const Ki   = params.Ki_spd  || 20;
  const J    = params.J_motor || 0.05;   // [kg·m²] 모터 관성
  const gr   = params.gear_ratio  || 9.5;
  const m    = params.m            || 1850; // [kg]
  const r    = params.r_wheel      || 0.33; // [m]
  // 모터 축 환산 등가 관성: J_eq = J_motor + m·r²/gr²
  const J_eq = J + m * r * r / (gr * gr);

  // 전류 루프 BW (속도 루프 플랜트 근사용)
  // [C1-Fix] 기본값 50 → 1.51 (실제 motor.js DEFAULT_PARAMS 값과 일치)
  //   50 은 33배 과대 → wc_inner 과대 → f_min/f_max 범위 왜곡
  const Kp_i     = params.Kp_id || 1.51;
  const L_d      = params.Ld    || 0.0003;
  const wc_inner = Kp_i / L_d;  // [rad/s]

  // ▶ 동적 주파수 범위
  // 0 dB 교차 예상: |C·Gi·Gm| ≈ Kp/(J_eq·ω²/wc_inner) = 1  → ω ≈ √(Kp·wc_inner/J_eq)
  // 올바른 이론 교차주파수: fc = Kp/(2π·J_eq)
  const f_c_spd    = Kp / (2 * Math.PI * J_eq);
  const f_min      = Math.max(0.001, f_c_spd / 200);  // fc의 1/200
  const f_max      = Math.min(10000, f_c_spd * 200);  // fc의 200배 (위상 거동 충분히 포함)

  const N_pts = 300;
  const data  = [];
  let pm = null, gm = null, bw3dB = null;
  let prevMag = null, prevPhase = null;
  let minPhase = 0;  // 위상 최솟값 추적 (저주파 -180° 근접도)

  for (let i = 0; i < N_pts; i++) {
    const f = f_min * Math.pow(f_max / f_min, i / (N_pts - 1));
    const w = 2 * Math.PI * f;

    // 전류 루프 근사: Gi(jω) = ωc_i/(jω + ωc_i) = ωc_i²/(ωc_i²+ω²) - j·ωc_i·ω/(ωc_i²+ω²)
    const dGi   = wc_inner*wc_inner + w*w;
    const Gi_re = wc_inner*wc_inner / dGi;
    const Gi_im = -wc_inner*w        / dGi;

    // 기계 플랜트: Gm(jω) = 1/(j·ω·J_eq) = 0 - j/(ω·J_eq)
    const Gm_re = 0;
    const Gm_im = -1 / (J_eq * w);

    // 전체 플랜트: P = Gi·Gm
    const P_re = Gi_re*Gm_re - Gi_im*Gm_im;
    const P_im = Gi_re*Gm_im + Gi_im*Gm_re;

    // 속도 PI 제어기: C(jω) = Kp - j·Ki/ω
    const C_re = Kp;
    const C_im = -Ki / w;

    // 개방루프: L = C·P
    const L_re = C_re*P_re - C_im*P_im;
    const L_im = C_re*P_im + C_im*P_re;

    const mag       = Math.sqrt(L_re*L_re + L_im*L_im);
    const mag_dB    = 20 * Math.log10(Math.max(mag, 1e-12));
    const phase_deg = Math.atan2(L_im, L_re) * 180 / Math.PI;
    if (phase_deg < minPhase) minPhase = phase_deg;

    // 폐루프 BW
    const one_pL_re = 1 + L_re;
    const dT2       = one_pL_re*one_pL_re + L_im*L_im;
    const T_re      = (L_re*one_pL_re + L_im*L_im) / dT2;
    const T_im      = L_im / dT2;
    const T_dB      = 20 * Math.log10(Math.max(Math.sqrt(T_re*T_re + T_im*T_im), 1e-12));
    if (bw3dB === null && T_dB < -3 && i > 0) bw3dB = f;

    // PM: 0 dB 교차
    if (prevMag !== null && prevMag >= 1 && mag < 1) {
      const alpha = (prevMag - 1) / (prevMag - mag);
      pm = 180 + prevPhase + (phase_deg - prevPhase) * alpha;
    }
    // GM: -180° 교차
    if (prevPhase !== null && prevPhase > -180 && phase_deg < -180) {
      const alpha  = (prevPhase + 180) / (prevPhase - phase_deg);
      const magInt = prevMag + (mag - prevMag) * alpha;
      gm = -20 * Math.log10(Math.max(magInt, 1e-12));
    }

    data.push({ freq_hz: +f.toFixed(4), mag_dB: +mag_dB.toFixed(2), phase_deg: +phase_deg.toFixed(2), T_dB: +T_dB.toFixed(2) });
    prevMag = mag; prevPhase = phase_deg;
  }

  // GM=null(위상이 -180° 미통과)는 이론적으로 무한대이나
  // 저주파에서 -180°에 얼마나 근접했는지(minPhase)를 별도 경고 지표로 제공
  const phase_margin_to_180 = 180 + minPhase; // 양수일수록 -180°로부터 여유
  const stable = (pm !== null && pm > 0) && (gm === null || gm > 0);

  // 이론 교차주파수 (개루프 0dB 교차)
  // ω<<wci 조건(속도BW≪전류BW)에서 Gi≈1 → |L|≈Kp/(ω·J_eq)
  // → 0dB: ω_c = Kp/J_eq → f_c = Kp/(2π·J_eq)
  // [수정] 기존 sqrt(Kp·wci/J_eq)/2π 는 Gi≠1 가정으로 틀린 값 (실제의 ~9배 과대)
  const bw_theory_spd = +(Kp / (2 * Math.PI * J_eq)).toFixed(2);   // Kp/(2π·J_eq) [Hz]
  const zero_freq_spd = +(Ki / Kp / (2 * Math.PI)).toFixed(2);      // PI 영점 Ki/Kp/2π [Hz]

  return {
    data,
    pm:                  pm     !== null ? +pm.toFixed(1)    : null,
    gm:                  gm     !== null ? +gm.toFixed(1)    : null,
    gm_inf:              gm === null,   // true = 위상 -180° 미통과 → GM=∞
    phase_margin_to_180: +phase_margin_to_180.toFixed(2),
    bw:                  bw3dB !== null  ? +bw3dB.toFixed(3) : null,
    bw_theory:           bw_theory_spd,  // 이론 교차주파수 (차트 기준선용)
    zero_freq:           zero_freq_spd,  // PI 영점 주파수
    f_range:             [+f_min.toFixed(4), +f_max.toFixed(1)],
    stable,
    Kp, Ki, J_eq, wc_inner,
  };
}

// ── computeNyquistData ──
/**
 * 전류·속도·위치 루프의 Nyquist 선도 데이터를 생성한다.
 *
 * 주파수 ω = 0 → ωmax (로그 스케일 600점)에 대해 개루프 L(jω)의 실수부/허수부를
 * 계산하고, 음수 주파수 축의 켤레 대칭을 mirror 복사하여 완전한 Nyquist 선도를 구성한다.
 * 복소평면에서 (-1+j0) 을 포위하는 횟수로 폐루프 안정성을 시각적으로 판단할 수 있다.
 * 표시 클램핑(|L| ≤ 8)을 적용하여 무한대 근방 발산을 방지한다.
 *
 * @param {object} params    차량/모터 파라미터
 * @param {string} loopType  'current' | 'speed' | 'position'
 * @returns {{ curve: Array<{re, im, w}>, unitCircle: Array<{re, im}>, loopType }}
 */
function computeNyquistData(params, loopType = 'current') {
  const PI2 = 2 * Math.PI;
  const dt = params.ctrl_dt || 1e-4;
  const T_delay = 1.5 * dt;

  // 플랜트 + 제어기 파라미터 선택
  let Kp, Ki, L, R, J_eq, wc_inner;
  if (loopType === 'speed') {
    Kp = params.Kp_spd || 143.4;
    Ki = params.Ki_spd || 3003;
    const Ld = params.Ld || 0.0003;
    const Kp_i = params.Kp_id || 1.51;
    wc_inner = Kp_i / Ld;
    const gr = params.gear_ratio || 9.5;
    const m  = params.m || 1850;
    const r  = params.r_wheel || 0.33;
    J_eq = (params.J_motor || 0.05) + m * r * r / (gr * gr);
  } else if (loopType === 'position') {
    Kp = params.Kp_pos || 5.0;  Ki = params.Ki_pos || 0.5;
    const Kp_s = params.Kp_spd || 143.4;
    const gr = params.gear_ratio || 9.5;
    const m  = params.m || 1850;
    const r  = params.r_wheel || 0.33;
    J_eq = (params.J_motor || 0.05) + m * r * r / (gr * gr);
    wc_inner = Kp_s / J_eq;  // [BugFix-3] 기존 *2 제거 — 다른 모든 함수(computeSpeedLoopBode, computeCascadeAnalysis 등)와 동일한 Kp_s/J_eq 공식으로 통일
  } else {
    Kp = params.Kp_iq || 3.02;  Ki = params.Ki_iq || 75;
    L  = params.Lq || params.Ld || 0.0003;
    R  = params.Rs || 0.015;
  }

  // 주파수 스윕: ω = -ωmax … +ωmax (Nyquist는 양/음 모두 필요)
  // [C6-Fix 연동] Nyquist wc_est 속도 루프도 동일 공식으로 통일
  const wc_est = loopType === 'current' ? (Kp / (L || 0.0003)) :
                 loopType === 'speed'   ? (Kp / (J_eq || 2)) :  // Kp/J_eq (√ 공식 제거)
                                          Kp * (wc_inner || 50);
  const w_max = Math.min(wc_est * 15, 2e6);
  const N = 600;
  const nyqCurve = [];    // [{re, im}] for scatter plot
  if (wc_est <= 0 || !isFinite(wc_est)) return { curve: [], unitCircle: [], loopType }; // [A32-BUG2] wc_est=0 가드

  for (let i = 0; i <= N; i++) {
    // log scale from w_max/1000 to w_max (positive ω)
    const w = Math.max((wc_est / 500), 1e-6) * Math.pow(Math.max(w_max / Math.max(wc_est / 500, 1e-6), 1), i / N);
    let L_re, L_im;

    if (loopType === 'current') {
      const dG = R * R + w * w * L * L;
      const G_re = R / dG, G_im = -w * L / dG;
      const C_re = Kp, C_im = -Ki / w;
      const D_re = Math.cos(w * T_delay), D_im = -Math.sin(w * T_delay);
      const CG_re = C_re*G_re - C_im*G_im, CG_im = C_re*G_im + C_im*G_re;
      L_re = CG_re*D_re - CG_im*D_im;
      L_im = CG_re*D_im + CG_im*D_re;
    } else if (loopType === 'speed') {
      const dGi = wc_inner*wc_inner + w*w;
      const Gi_re = wc_inner*wc_inner/dGi, Gi_im = -wc_inner*w/dGi;
      const Gm_re = 0, Gm_im = -1/(J_eq*w);
      const P_re = Gi_re*Gm_re - Gi_im*Gm_im, P_im = Gi_re*Gm_im + Gi_im*Gm_re;
      L_re = Kp*P_re - (-Ki/w)*P_im;
      L_im = Kp*P_im + (-Ki/w)*P_re;
    } else {
      // position: outer PI * (speed closed-loop ≈ 1st order)
      const wcs = wc_inner; // speed BW approx
      const Gs_re = wcs*wcs/(wcs*wcs+w*w), Gs_im = -wcs*w/(wcs*wcs+w*w);
      const Gint_re = 0, Gint_im = -1/w; // integrator 1/s
      const P_re = Gs_re*Gint_re - Gs_im*Gint_im, P_im = Gs_re*Gint_im + Gs_im*Gint_re;
      L_re = Kp*P_re - (-Ki/w)*P_im;
      L_im = Kp*P_im + (-Ki/w)*P_re;
    }

    // clamp for display
    const mag = Math.sqrt(L_re*L_re+L_im*L_im);
    const clamp = 8;
    const scale = mag > clamp ? clamp/mag : 1;
    nyqCurve.push({ re: +(L_re*scale).toFixed(4), im: +(L_im*scale).toFixed(4), w: +w.toFixed(1) });
    // mirror (negative freq)
    nyqCurve.push({ re: +(L_re*scale).toFixed(4), im: +(-L_im*scale).toFixed(4), w: +(-w).toFixed(1) });
  }

  // Unit circle for reference
  const unitCircle = Array.from({length: 73}, (_, i) => {
    const a = i * PI2 / 72;
    return { re: +Math.cos(a).toFixed(3), im: +Math.sin(a).toFixed(3) };
  });

  return { curve: nyqCurve, unitCircle, loopType };
}

// ── computeRootLocus ──
/**
 * 전류·속도·위치 루프의 근궤적(Root Locus)을 계산한다.
 *
 * K를 0에서 최대값까지 300점 스윕하면서 폐루프 특성다항식의 근을 해석적으로 구한다.
 * 전류 루프(2차): 이차방정식 판별식으로 직접 계산.
 * 속도·위치 루프(3차): Cardano-삼각법(trigonometric method)으로 3개 근을 계산.
 * 복소 켤레 근 쌍은 허수부 부호를 반전한 두 개의 점으로 표현된다.
 *
 * @param {object} params    차량/모터 파라미터
 * @param {string} loopType  'current' | 'speed' | 'position'
 * @returns {{ loci, poles, zeros, loopType, note }}
 *   loci: 각 K에 대한 근 좌표 배열 [{k, r1, i1, r2, i2, ...}]
 */
function computeRootLocus(params, loopType = 'current') {
  // Open loop: L(s) = K * N(s) / D(s)
  // Current loop: L = K*(s+z)/(s*(s+p))  z=Ki/Kp, p=R/L
  // Speed loop:   L = K*(s+z)/(s^2*(s+wci)) — 2nd order denom (approx)
  const loci = [];

  if (loopType === 'current') {
    const Kp = params.Kp_iq || 3.02;
    const Ki = params.Ki_iq || 75;
    const L  = params.Lq   || 0.0003;
    const R  = params.Rs   || 0.015;
    const z  = Ki / Kp;      // PI zero: -z
    const p  = R  / L;       // Plant pole: -p

    // CL char poly: L·s²+(R+K)·s+K·(Ki/Kp)=0  [K = Kp-variation]
    // Normalize: s²+(p+K/L)·s+(K/L)·z = 0
    const K_vals = Array.from({length: 300}, (_, i) => (i / 299) * Kp * 4);
    for (const K of K_vals) {
      const a = 1, b = p + K / L, c = (K / L) * z;
      const disc = b*b - 4*a*c;
      if (disc >= 0) {
        loci.push({ k: +K.toFixed(2), r1: +( (-b+Math.sqrt(disc))/(2*a)).toFixed(4), i1: 0,
                    r2: +((-b-Math.sqrt(disc))/(2*a)).toFixed(4), i2: 0 });
      } else {
        loci.push({ k: +K.toFixed(2), r1: +(-b/(2*a)).toFixed(4), i1: +(Math.sqrt(-disc)/(2*a)).toFixed(4),
                    r2: +(-b/(2*a)).toFixed(4), i2: +(-Math.sqrt(-disc)/(2*a)).toFixed(4) });
      }
    }
    return { loci, poles: [0, -p], zeros: [-z], loopType,
             note: `PI zero: ${z.toFixed(1)} rad/s | Plant pole: ${p.toFixed(1)} rad/s` };

  } else if (loopType === 'speed') {
    const Kp  = params.Kp_spd || 143.4;
    const Ki  = params.Ki_spd || 3003;
    const Kp_i= params.Kp_id  || 1.51;
    const Ld  = params.Ld     || 0.0003;
    const gr  = params.gear_ratio || 9.5;
    const m   = params.m       || 1850;
    const rw  = params.r_wheel || 0.33;
    const J_eq= (params.J_motor||0.05) + m*rw*rw/(gr*gr);
    const wci = Kp_i / Ld;
    const z   = Ki / Kp;

    // CL char poly (3rd order): J_eq·s³ + (J_eq·wci)·s² + (K·wci/1)·s + (K·wci·z) = 0
    // Normalized: s³ + wci·s² + (K/J_eq)·wci·s + (K/J_eq)·wci·z = 0
    const K_vals = Array.from({length: 300}, (_, i) => (i / 299) * Kp * 3);
    for (const K of K_vals) {
      const a3=1, a2=wci, a1=K/J_eq*wci, a0=K/J_eq*wci*z;
      // solve cubic via depressed cubic (Cardano/trigonometric method)
      const shift = -a2 / (3 * a3);
      const p_coef = (3*a3*a1 - a2*a2) / (3*a3*a3);
      const q_coef = (2*a2*a2*a2 - 9*a3*a2*a1 + 27*a3*a3*a0) / (27*a3*a3*a3);
      const Dcubic = -(4*p_coef*p_coef*p_coef + 27*q_coef*q_coef);
      if (Dcubic > 0) {
        // 3 distinct real roots
        const m3 = 2*Math.sqrt(-p_coef/3);
        const theta3 = Math.acos(Math.max(-1, Math.min(1, 3*q_coef/(p_coef*m3)))) / 3; // [A32-BUG3] acos 범위 클램프
        const roots = [
          m3*Math.cos(theta3) + shift,
          m3*Math.cos(theta3 - 2*Math.PI/3) + shift,
          m3*Math.cos(theta3 + 2*Math.PI/3) + shift,
        ].sort((a,b)=>a-b);
        loci.push({ k:+K.toFixed(1), r1:+roots[0].toFixed(2), i1:0, r2:+roots[1].toFixed(2), i2:0, r3:+roots[2].toFixed(2), i3:0 });
      } else {
        // 1 real, 2 complex conjugate
        const sqD = Math.sqrt(Math.max(0, -Dcubic/108));
        const inner = -q_coef/2;
        const cbrtArg1 = inner + sqD, cbrtArg2 = inner - sqD;
        const u = cbrtArg1 >= 0 ? Math.cbrt(cbrtArg1) : -Math.cbrt(-cbrtArg1);
        const v = cbrtArg2 >= 0 ? Math.cbrt(cbrtArg2) : -Math.cbrt(-cbrtArg2);
        const realRoot = (u + v) + shift;
        const reComplex = -(u + v)/2 + shift;
        const imComplex = (u - v)*Math.sqrt(3)/2;
        loci.push({ k:+K.toFixed(1), r1:+realRoot.toFixed(2), i1:0,
                    r2:+reComplex.toFixed(2), i2:+imComplex.toFixed(2),
                    r3:+reComplex.toFixed(2), i3:+(-imComplex).toFixed(2) });
      }
    }
    return { loci, poles: [0, 0, -wci], zeros: [-z], loopType,
             note: `PI zero: ${z.toFixed(0)} rad/s | Inner BW: ${(wci/(2*Math.PI)).toFixed(0)} Hz` };

  } else if (loopType === 'position') {
    // ── [Impl-1] 위치 루프 근궤적 ──────────────────────────────────────────
    // 위치 루프 PID: C_p(s) = Kp_p + Ki_p/s + Kd_p·s = (Kd_p·s² + Kp_p·s + Ki_p) / s
    // 플랜트: 속도 CL(1차 근사) × 위치 적분기 = wcs/(s+wcs) × 1/s
    // 개루프: L(s) = K · (zd·s² + s + zi) / (s²·(s + wcs))
    //   여기서 K = Kp_p (스윕), zd = Kd_p/Kp_p, zi = Ki_p/Kp_p
    //
    // 폐루프 특성다항식 (K를 변수로 분리):
    //   s²·(s + wcs) + K·(zd·s² + s + zi) = 0
    //   s³ + (wcs + K·zd)·s² + K·s + K·zi  = 0
    //   → 3차 다항식: Cardano/삼각법으로 풀이 (speed loop 솔버 재사용)

    const Kp_p  = params.Kp_pos || 5.0;
    const Ki_p  = params.Ki_pos || 0.5;
    const Kd_p  = params.Kd_pos || 0.05;
    const Kp_s  = params.Kp_spd || 143.4;
    const gr    = params.gear_ratio || 9.5;
    const m     = params.m || 1850;
    const rw    = params.r_wheel || 0.33;
    const J_eq  = (params.J_motor || 0.05) + m * rw * rw / (gr * gr);
    const wcs   = Kp_s / J_eq;  // 속도 루프 BW [rad/s]

    const zd  = Kd_p / Math.max(Kp_p, 1e-9);  // D/P 비율
    const zi  = Ki_p / Math.max(Kp_p, 1e-9);  // I/P 비율

    // 개루프 영점 (PID numerator 근): zd·s² + s + zi = 0
    const disc_z = 1 - 4 * zd * zi;
    const zeros_pos = disc_z >= 0
      ? [ +( (-1 + Math.sqrt(disc_z)) / (2 * zd) ).toFixed(3),
          +( (-1 - Math.sqrt(disc_z)) / (2 * zd) ).toFixed(3) ]
      : [];  // 복소 영점은 표시 생략

    const K_vals = Array.from({ length: 300 }, (_, i) => (i / 299) * Kp_p * 4);
    for (const K of K_vals) {
      // 특성다항식 계수: s³ + (wcs+K·zd)·s² + K·s + K·zi
      const a3 = 1, a2 = wcs + K * zd, a1 = K, a0 = K * zi;

      const shift   = -a2 / (3 * a3);
      const p_coef  = (3 * a3 * a1 - a2 * a2) / (3 * a3 * a3);
      const q_coef  = (2 * a2**3 - 9 * a3 * a2 * a1 + 27 * a3**2 * a0) / (27 * a3**3);
      const Dcubic  = -(4 * p_coef**3 + 27 * q_coef**2);

      if (Dcubic > 0) {
        const m3 = 2 * Math.sqrt(-p_coef / 3);
        const theta3 = Math.acos(Math.max(-1, Math.min(1, 3 * q_coef / (p_coef * m3)))) / 3;
        const roots = [
          m3 * Math.cos(theta3) + shift,
          m3 * Math.cos(theta3 - 2 * Math.PI / 3) + shift,
          m3 * Math.cos(theta3 + 2 * Math.PI / 3) + shift,
        ].sort((a, b) => a - b);
        loci.push({ k: +K.toFixed(3), r1: +roots[0].toFixed(3), i1: 0,
                    r2: +roots[1].toFixed(3), i2: 0, r3: +roots[2].toFixed(3), i3: 0 });
      } else {
        const sqD = Math.sqrt(Math.max(0, -Dcubic / 108));
        const inner = -q_coef / 2;
        const cbrtArg1 = inner + sqD, cbrtArg2 = inner - sqD;
        const u = cbrtArg1 >= 0 ? Math.cbrt(cbrtArg1) : -Math.cbrt(-cbrtArg1);
        const v = cbrtArg2 >= 0 ? Math.cbrt(cbrtArg2) : -Math.cbrt(-cbrtArg2);
        const realRoot  = (u + v) + shift;
        const reComplex = -(u + v) / 2 + shift;
        const imComplex = (u - v) * Math.sqrt(3) / 2;
        loci.push({ k: +K.toFixed(3), r1: +realRoot.toFixed(3), i1: 0,
                    r2: +reComplex.toFixed(3), i2: +imComplex.toFixed(3),
                    r3: +reComplex.toFixed(3), i3: +(-imComplex).toFixed(3) });
      }
    }
    return {
      loci,
      poles: [0, 0, -wcs],   // s²·(s+wcs) → 이중 원점극 + 속도 CL 극
      zeros: zeros_pos,
      loopType,
      note: `PID zeros: zd=${zd.toFixed(4)}, zi=${zi.toFixed(4)} | Speed CL pole: −${wcs.toFixed(1)} rad/s`,
    };
  }
  return { loci: [], poles: [], zeros: [], loopType, note: '' };
}

// ── computeZDomainData ──
/**
 * 이산 Z 도메인에서 전류·속도 루프의 주파수 응답과 안정성을 계산한다.
 *
 * PI 제어기는 Bilinear(Tustin) 변환으로 이산화하고,
 * 플랜트는 ZOH(Zero-Order Hold) 변환으로 이산화한다.
 * z = e^(jθ) 위에서 개루프 L(z) = C(z)·P(z) 의 Bode 선도를 250점 계산한다.
 * 폐루프 안정성은 이차방정식 특성근의 |z| < 1 여부로 판정한다(stable_hint).
 *
 * @param {object} params    차량/모터 파라미터
 * @param {string} loopType  'current' | 'speed'
 * @returns {{ freqData, unitCircle, openLoopPoles, openLoopZeros,
 *             zeroC, poleP, gainP, a0, a1, T_s, stable_hint, clPoles_mag, loopType }}
 */
function computeZDomainData(params, loopType = 'current') {
  const T = params.ctrl_dt || 1e-4;
  const PI2 = 2 * Math.PI;

  let Kp, Ki, L, R, J_eq, wc_inner;  // [BugFix-1] wc_inner을 let 선언에 추가 (기존: 암묵적 전역 / strict mode ReferenceError)
  if (loopType === 'current') {
    Kp = params.Kp_iq || 3.02;  Ki = params.Ki_iq || 75;
    L  = params.Lq || 0.0003;   R  = params.Rs || 0.015;
  } else {
    Kp = params.Kp_spd || 143.4;  Ki = params.Ki_spd || 3003;
    const Kp_i = params.Kp_id || 1.51;
    const Ld   = params.Ld    || 0.0003;
    wc_inner   = Kp_i / Ld;
    const gr = params.gear_ratio || 9.5;
    const m  = params.m || 1850;
    const rw = params.r_wheel || 0.33;
    J_eq = (params.J_motor || 0.05) + m*rw*rw/(gr*gr);
  }

  // ── 이산 PI 제어기 (Bilinear/Tustin 변환) ──────────────────
  // C(z) = (Kp + Ki*T/2)·(z + (Ki*T/2-Kp)/(Kp+Ki*T/2)) / (z-1)
  const a0 = Kp + Ki * T / 2;       // numerator leading
  const a1 = -(Kp - Ki * T / 2);    // numerator trailing (note sign)
  const poleC = 1;                    // denominator pole
  const zeroC = -(Kp - Ki*T/2) / (Kp + Ki*T/2); // numerator zero

  // ── 이산 플랜트 (ZOH 변환) ──────────────────────────────────
  let poleP, gainP;
  if (loopType === 'current') {
    const tau = L / R;
    poleP  = Math.exp(-T / tau);          // e^(-T/τ)
    gainP  = (1 - poleP) / R;             // ZOH gain
  } else {
    // Speed loop: mechanical integrator Gm = 1/(J_eq*s)
    // ZOH of 1/s → T·z^-1 / (1-z^-1) → pole at z=1
    poleP = 1;
    gainP = T / (J_eq || 2);
    // [BugFix-2] 전류 루프 2샘플 지연 z^(-2) 반영:
    //   기존: 주석만 있고 gainP에 미적용 → 이산 안정성 낙관적 과평가
    //   수정: gainP에 wc_inner 기반 1차 근사 지연 계수 적용
    //         z^(-2) ≈ (wc_i*T)^2 이득 감쇠 (저주파 근사), 이산 전달함수: wc_i²T²/(1-z^-1)^0 근사
    //   실용 처리: gainP_eff = gainP * (wc_inner*T)² / (1 + (wc_inner*T)²)
    //   → 전류 루프 BW 대비 낮은 주파수에서 ≈gainP, 높은 주파수에서 감쇠
    if (wc_inner && wc_inner > 0) {
      const wcT = wc_inner * T;
      gainP = gainP * (wcT * wcT) / (1 + wcT * wcT);
    }
  }

  // ── 개방루프 Z극/영점 ──────────────────────────────────────
  const openLoopPoles = [poleC, poleP];
  const openLoopZeros = [zeroC];

  // ── 단위원 + 주파수 응답 (z = e^jθ) ─────────────────────────
  const freqData = [];
  const N = 250;
  for (let i = 0; i <= N; i++) {
    const theta = (i / N) * Math.PI; // 0 to π (normalized 0 to fs/2)
    const f_norm = i / N;            // 0 to 1 (Nyquist)
    const zRe = Math.cos(theta), zIm = Math.sin(theta);

    // C(z): (a0*z + a1) / (z - 1)
    // evaluate at z = e^jθ
    const numC_re = a0*zRe + (-Kp + Ki*T/2), numC_im = a0*zIm;
    const denC_re = zRe - 1, denC_im = zIm;
    const denC2 = denC_re*denC_re + denC_im*denC_im + 1e-30;
    const C_re = (numC_re*denC_re + numC_im*denC_im)/denC2;
    const C_im = (numC_im*denC_re - numC_re*denC_im)/denC2;

    // P(z): gainP / (z - poleP)
    const denP_re = zRe - poleP, denP_im = zIm;
    const denP2 = denP_re*denP_re + denP_im*denP_im + 1e-30;
    const P_re = gainP*denP_re/denP2;
    const P_im = -gainP*denP_im/denP2;

    // Open-loop L(z) = C(z)*P(z)
    const L_re = C_re*P_re - C_im*P_im;
    const L_im = C_re*P_im + C_im*P_re;

    const mag_dB = 20*Math.log10(Math.max(Math.sqrt(L_re*L_re+L_im*L_im), 1e-12));
    const phase_deg = Math.atan2(L_im, L_re) * 180 / Math.PI;

    // Closed-loop |T(z)|
    const one_pL_re = 1+L_re, dT2 = one_pL_re*one_pL_re + L_im*L_im + 1e-30;
    const T_mag = Math.sqrt((L_re*one_pL_re+L_im*L_im)**2 + (L_im)**2) / dT2;
    const T_dB  = 20*Math.log10(Math.max(T_mag, 1e-12));

    freqData.push({ f_norm: +f_norm.toFixed(3), mag_dB: +mag_dB.toFixed(2),
                    phase_deg: +phase_deg.toFixed(2), T_dB: +T_dB.toFixed(2) });
  }

  // ── 단위원 마커 ──────────────────────────────────────────────
  const unitCircle = Array.from({length: 73}, (_, i) => {
    const a = i * PI2 / 72;
    return { re: +Math.cos(a).toFixed(3), im: +Math.sin(a).toFixed(3) };
  });

  // [C5-Fix] stable_hint — 개루프 극점(항상 1.0) 대신 폐루프 극점 계산
  //   기존: openLoopPoles = [poleC=1, poleP] → maxPoleMag=1 → 항상 true (무의미)
  //   수정: 이산 폐루프 특성방정식 z² + α1·z + α2 = 0 의 근 |z|<1 여부로 판정
  //
  //   개루프: L(z) = C(z)·P(z) = a0·gainP / ((z-1)(z-poleP)) · z + (a1/a0)
  //   폐루프: 특성방정식 = (z-1)(z-poleP) + a0·gainP·(z + a1/a0) = 0
  //         = z² + [-(1+poleP) + a0·gainP]·z + [poleP - a0·gainP·(-a1/a0)]
  //         = z² + [a0·gainP - 1 - poleP]·z + [poleP + a1·gainP]
  const cl_b = a0 * gainP - 1 - poleP;         // z¹ 계수
  const cl_c = poleP + a1 * gainP;              // z⁰ 계수 (note: a1 includes sign)
  // 이차방정식 근: z = (-b ± √(b²−4c)) / 2
  const disc_cl = cl_b * cl_b - 4 * cl_c;
  let clPoles_mag;
  if (disc_cl >= 0) {
    const r1 = (-cl_b + Math.sqrt(disc_cl)) / 2;
    const r2 = (-cl_b - Math.sqrt(disc_cl)) / 2;
    clPoles_mag = [Math.abs(r1), Math.abs(r2)];
  } else {
    // 복소 켤레 근: |z| = √c
    const mag_cl = Math.sqrt(Math.max(0, cl_c));
    clPoles_mag = [mag_cl, mag_cl];
  }
  const maxPoleMag = Math.max(...clPoles_mag);

  return {
    freqData, unitCircle, openLoopPoles, openLoopZeros,
    zeroC: +zeroC.toFixed(4), poleP: +poleP.toFixed(4),
    gainP: +gainP.toFixed(6), a0: +a0.toFixed(4), a1: +a1.toFixed(4),
    T_s: T,
    stable_hint: maxPoleMag < 1.0,   // [C5-Fix] 폐루프 극점 |z|<1 기준
    clPoles_mag: clPoles_mag.map(m => +m.toFixed(5)),
    maxPoleMag: +maxPoleMag.toFixed(5),
    loopType, Kp, Ki,
  };
}

// ── simAntiWindup ──
/**
 * Anti-windup 유무에 따른 PI 제어기 단계 응답을 시뮬레이션하고 비교한다.
 *
 * 기준값 r=1의 단계 입력에 대해 두 가지 PI 시뮬레이션을 병렬로 수행한다.
 * withWindup: 단순 PI + 포화(anti-windup 없음) — 적분 항이 포화 중에도 계속 누적되어 오버슈트 발생.
 * withoutWindup: back-calculation 방식 — 포화 오차 (u_sat - u_raw)를 추적 시정수 Tt로 나눠
 * 적분기에 피드백하여 와인드업을 억제한다 (Tt = √(Kp/Ki)).
 * 두 시뮬레이션의 오버슈트와 정착 시간을 KPI로 반환한다.
 *
 * @param {object} params    차량/모터 파라미터
 * @param {string} loopType  'current' | 'speed'
 * @returns {{ withWindup, withoutWindup, u_max, kpi, loopType, Kp, Ki, tau }}
 */
function simAntiWindup(params, loopType = 'current') {
  // 단계 응답: 기준값 r=1 → 포화 → 응답 비교
  // 플랜트: 1차 (전류) 또는 2차 (속도)
  let Kp, Ki, tau, K_plant, u_max;
  if (loopType === 'current') {
    Kp = params.Kp_iq || 3.02;  Ki = params.Ki_iq || 75;
    tau = (params.Lq || 0.0003) / (params.Rs || 0.015);  // RL time constant
    K_plant = 1 / (params.Rs || 0.015);
    u_max = (params.V_dc || 400) / Math.sqrt(3); // peak phase voltage
  } else {
    Kp = params.Kp_spd || 143.4;  Ki = params.Ki_spd || 3003;
    const gr = params.gear_ratio || 9.5;
    const m  = params.m || 1850;
    const rw = params.r_wheel || 0.33;
    const J  = (params.J_motor || 0.05) + m*rw*rw/(gr*gr);
    tau = J / Math.max(Kp, 1e-6); // [A32-BUG4] Kp=0 div/0 가드
    K_plant = 1 / J;
    u_max = (params.T_max_motor || 320) * 0.8; // torque limit
  }

  const dt = params.ctrl_dt || 1e-4;
  const T_sim = Math.min(tau * 10, 0.5);  // simulate 10 time constants
  const N_steps = Math.ceil(T_sim / dt);
  const step = Math.max(1, Math.floor(N_steps / 400)); // downsample to ~400 pts

  const withWindup    = [];
  const withoutWindup = [];

  // ── Simulation without anti-windup ──────────────────────────
  let y = 0, integ = 0;  // [Fix-④] y_dot dead code 제거
  for (let k = 0; k < N_steps; k++) {
    const r = 1.0; // step reference
    const e = r - y;
    integ += e * dt;
    const u_raw = Kp * e + Ki * integ;
    const u = Math.min(u_max, Math.max(-u_max, u_raw));   // saturate output
    // plant update (1st order Euler): dy = (K_plant*u - y/tau)*dt
    y = y + (K_plant * u - y / tau) * dt;
    if (k % step === 0) withWindup.push({ t: +(k*dt).toFixed(5), y: +y.toFixed(5), u: +u.toFixed(3), integ: +integ.toFixed(4) });
  }

  // ── Simulation WITH anti-windup (back-calculation) ──────────
  y = 0; integ = 0;
  const Tt = Math.sqrt(Kp / Math.max(Ki, 1e-6)); // tracking time constant
  for (let k = 0; k < N_steps; k++) {
    const r = 1.0;
    const e = r - y;
    const u_raw = Kp * e + Ki * integ;
    const u_sat = Math.min(u_max, Math.max(-u_max, u_raw));
    const e_aw = u_sat - u_raw;               // anti-windup error
    integ += (e + e_aw / Tt) * dt;             // back-calculation
    y = y + (K_plant * u_sat - y / tau) * dt;
    if (k % step === 0) withoutWindup.push({ t: +(k*dt).toFixed(5), y: +y.toFixed(5), u: +u_sat.toFixed(3), integ: +integ.toFixed(4) });
  }

  // KPI
  const ss_y_w = withWindup[withWindup.length - 1]?.y || 0;
  const ss_y_n = withoutWindup[withoutWindup.length - 1]?.y || 0;
  const overshoot_w = Math.max(...withWindup.map(d => d.y)) - ss_y_w;
  const overshoot_n = Math.max(...withoutWindup.map(d => d.y)) - ss_y_n;
  const settle_idx_w = withWindup.findIndex(d => Math.abs(d.y - ss_y_w) < 0.02 * Math.abs(ss_y_w));
  const settle_idx_n = withoutWindup.findIndex(d => Math.abs(d.y - ss_y_n) < 0.02 * Math.abs(ss_y_n));

  return {
    withWindup, withoutWindup,
    u_max,
    kpi: {
      overshoot_w: +(overshoot_w * 100 / Math.max(ss_y_w, 0.001)).toFixed(1),
      overshoot_n: +(overshoot_n * 100 / Math.max(ss_y_n, 0.001)).toFixed(1),
      settle_w: settle_idx_w >= 0 ? +(withWindup[settle_idx_w].t).toFixed(5) : null,
      settle_n: settle_idx_n >= 0 ? +(withoutWindup[settle_idx_n].t).toFixed(5) : null,
    },
    loopType, Kp, Ki, tau,
  };
}

// ── computeAutoTuning ──
// [Code-Quality-2] _method → method 로 변경. 항상 4가지 방법 전부 계산하되,
//   호출자가 전달한 method를 results.active_method로 반환해 UI가 강조 표시할 수 있도록 함.
//   이렇게 하면 API 시그니처가 의도를 명확히 표현하면서도 모든 결과를 한번에 얻는 현재 구조 유지.
/**
 * Ziegler-Nichols, IMC, SIMC, Cohen-Coon 4가지 방법으로 PI 자동 튜닝을 수행한다.
 *
 * 각 루프의 FOPDT 플랜트 모델(K_p, τ_p, θ_p)을 추정한 뒤
 * 4가지 고전적 PID 튜닝 공식을 적용하여 Kp·Ki 파라미터를 산출한다.
 * IMC: λ(원하는 CL 시정수)를 조정해 빠른 응답과 안정성을 균형 설정.
 * SIMC: Skogestad의 단순화 IMC — Ti = min(τ_p, 4·(λ+θ)) 로 적분 와인드업 완화.
 * ZN: 임계 이득 Ku·주기 Tu 를 수치 반복법으로 산출 후 0.45Ku/1.2Tu 공식 적용.
 * Cohen-Coon: 지연/시정수 비율 r=θ/τ를 사용하는 비율 기반 공식.
 * 속도 루프는 순적분 플랜트(G=K/s)를 Skogestad 적분기 규칙으로 처리한다.
 *
 * @param {object} params    차량/모터 파라미터
 * @param {string} loopType  'current' | 'speed' | 'position'
 * @param {string} method    UI 강조 표시용 활성 메서드 ('imc'|'simc'|'zn'|'cc')
 * @returns {{ results: {imc, simc, zn, cc, current}, K_p, tau_p, theta_p, loopType, active_method }}
 */
function computeAutoTuning(params, loopType = 'current', method = 'imc') {
  // Plant parameters
  let K_p, tau_p, theta_p; // steady-state gain, time constant, dead time
  if (loopType === 'current') {
    K_p   = 1 / (params.Rs || 0.015);
    tau_p = (params.Lq || 0.0003) / (params.Rs || 0.015);
    theta_p = 1.5 * (params.ctrl_dt || 1e-4);  // computation delay
  } else if (loopType === 'speed') {
    const gr = params.gear_ratio || 9.5;
    const m  = params.m || 1850;
    const rw = params.r_wheel || 0.33;
    const J_eq = (params.J_motor || 0.05) + m*rw*rw/(gr*gr);
    const Kp_i = params.Kp_id || 1.51;
    const Ld   = params.Ld    || 0.0003;
    const wci  = Kp_i / Ld;
    // [C7-Fix] 속도 루프 플랜트 파라미터 교정
    //   기존: tau_p = J_eq / Kp_i — 차원 오류 [kg·m²/(V/A)=A·s²/V ≠ s]
    //         FOPDT 가정 자체도 부적합 (순적분 플랜트 1/(J·s), 시정수 τ→∞)
    //   수정: 순적분 플랜트 G(s) = K_p/s = (1/J_eq)/s 으로 모델링
    //         → 딜레이 τ_d = 3/wci (전류 루프 지연 근사)만 의미 있음
    //         ZN/IMC/SIMC 공식은 τ_p = 무한대 처리 → 실용적 상한 클램프
    //         Kp_ZN ≈ K_p·ω_u / (π·(1+ω_u²·τ_d²)^0.5) 에서 τ_d만 사용
    K_p     = 1 / J_eq;                   // [rad/s per N·m]
    tau_p   = Math.min(J_eq / 0.1, 50);  // 순적분 근사: τ=J_eq/damping, 클램프 50s
    theta_p = 3 / Math.max(wci, 1);       // 전류 루프 지연 근사
  } else {
    // position loop: 속도 CL(≈1차) 뒤에 위치 적분기 → G_pos(s) ≈ 1/s
    // [Fix-AutoTune-Pos] 더미값(1.0/0.1/0.01) → 속도 루프 BW 기반 실제 파라미터
    //   K_p = 1 (정규화 이득, 1/s 플랜트)
    //   τ_d = 3/wcs  (속도 루프 등가 지연 — computeAutoTuning C7-Fix와 동일 패턴)
    //   tau_p: 순적분이므로 τ→∞, 실용 상한 클램프 사용
    const gr_p     = params.gear_ratio || 9.5;
    const m_p      = params.m          || 1850;
    const rw_p     = params.r_wheel    || 0.33;
    const J_p      = (params.J_motor   || 0.05) + m_p * rw_p * rw_p / (gr_p * gr_p);
    const Kp_s_nom = params.Kp_spd     || 143.4;
    const wcs_nom  = Kp_s_nom / J_p;           // 속도 루프 BW [rad/s]
    K_p     = 1.0;
    tau_p   = Math.min(4 / Math.max(wcs_nom * 0.01, 0.001), 20); // 실용 상한 20s
    theta_p = 3 / Math.max(wcs_nom, 1);        // 속도 루프 등가 지연 [s]
  }

  const results = {};

  // ─── Ziegler-Nichols (continuous cycling) ───────────────────
  // Find Ku: gain at PM=0 for plant 1/(tau*s+1)·e^(-theta*s)
  // Simplified: Ku ≈ π/(2*K_p) * √(1 + (π·tau/(2·theta))²) / (π/2)
  // More practical: Ku = 1/(K_p * |G(jωu)| where ∠G(jωu) = -180°
  // For 1st order + delay: ωu·theta = π - arctan(ωu·tau)
  // Solve numerically
  let omega_u = Math.PI / (2 * theta_p); // initial guess
  for (let iter = 0; iter < 50; iter++) {
    const angle = -Math.atan(omega_u * tau_p) - omega_u * theta_p;
    if (Math.abs(angle + Math.PI) < 1e-4) break;
    omega_u *= 1 + (angle + Math.PI) / (5 * Math.PI);
    if (omega_u <= 0) { omega_u = 1e-3; break; }
  }
  const G_mag_u = K_p / Math.sqrt(1 + omega_u * omega_u * tau_p * tau_p);
  const Ku = 1 / Math.max(G_mag_u, 1e-12);
  const Tu = 2 * Math.PI / omega_u;
  const ZN = { Kp: +(0.45 * Ku).toFixed(4), Ki: +(0.45 * Ku / (Tu / 1.2)).toFixed(4), Ti: +(Tu / 1.2).toFixed(5) };
  results.zn = { ...ZN, Ku: +Ku.toFixed(4), Tu: +Tu.toFixed(6), omega_u: +omega_u.toFixed(1), method: 'Ziegler-Nichols' };

  // ─── IMC (Internal Model Control) ───────────────────────────
  // λ = desired closed-loop time constant (= tau_c)
  // For 1st order: Kp = tau/(K_p*(λ+theta)), Ki = Kp/(tau)
  const lambda_imc = loopType === 'current' ? (params.ctrl_dt || 1e-4) * 5 :
                     loopType === 'speed'   ? 0.02 : 0.5;
  const Kp_imc = tau_p / (K_p * (lambda_imc + theta_p));
  const Ti_imc = tau_p;
  const IMC = { Kp: +Kp_imc.toFixed(4), Ki: +(Kp_imc / Ti_imc).toFixed(4), Ti: +Ti_imc.toFixed(5), lambda: +lambda_imc.toFixed(6) };
  results.imc = { ...IMC, method: 'IMC' };

  // ─── SIMC (Skogestad IMC) ────────────────────────────────────
  const lambda_simc = Math.max(theta_p, tau_p * 0.1);
  const Kp_simc = (1 / (K_p * (lambda_simc + theta_p))) * Math.min(tau_p, 4*(lambda_simc+theta_p));
  const Ti_simc = Math.min(tau_p, 4 * (lambda_simc + theta_p));
  const SIMC = { Kp: +Kp_simc.toFixed(4), Ki: +(Kp_simc / Ti_simc).toFixed(4), Ti: +Ti_simc.toFixed(5) };
  results.simc = { ...SIMC, method: 'SIMC' };

  // ─── Cohen-Coon ──────────────────────────────────────────────
  const r_cc = theta_p / Math.max(tau_p, 1e-9); // [A32-BUG5] tau_p=0 overflow 가드
  const Kp_cc = (1 / (K_p * theta_p)) * (0.9 + r_cc / 12);
  const Ti_cc = theta_p * (30 + 3 * r_cc) / (9 + 20 * r_cc);
  const CC = { Kp: +Kp_cc.toFixed(4), Ki: +(Kp_cc / Ti_cc).toFixed(4), Ti: +Ti_cc.toFixed(5) };
  results.cc = { ...CC, method: 'Cohen-Coon' };

  // ─── 현재 파라미터 ──────────────────────────────────────────
  let Kp_now, Ki_now;
  if (loopType === 'current') { Kp_now = params.Kp_iq || 3.02; Ki_now = params.Ki_iq || 75; }
  else { Kp_now = params.Kp_spd || 143.4; Ki_now = params.Ki_spd || 3003; }
  results.current = { Kp: Kp_now, Ki: Ki_now, Ti: +(Kp_now/Math.max(Ki_now,1e-6)).toFixed(5), method: 'Current (configured)' };

  // active_method: 호출자가 선택한 방법을 그대로 반환 (UI 강조 표시용)
  const VALID_METHODS = ['imc', 'simc', 'zn', 'cc'];
  const active_method = VALID_METHODS.includes(method) ? method : 'imc';
  return { results, K_p, tau_p, theta_p, loopType, active_method };
}

// ── computeMRAC ──
/**
 * σ-modification MRAC(모델 기준 적응 제어)를 시뮬레이션하고 순수 MIT 규칙과 비교한다.
 *
 * 기준 모델 ẋ_m = am·x_m + bm·r 에 대해 플랜트 파라미터를 실시간 적응한다.
 * 순수 MIT 규칙(Ioannou & Sun 1996): θ̇ = -γ·e·ψ — 유계 입력에서 파라미터 드리프트 위험.
 * σ-modification: θ̇ = -γ·e·ψ - σ·θ 추가 감쇠항으로 파라미터 드리프트를 억제한다.
 * e-modification: |e| < e_thresh 이면 업데이트를 스킵하여 노이즈 대역을 차단한다.
 * 두 방법의 정상상태 추적 오차와 게인 드리프트 크기를 비교하여 반환한다.
 *
 * @param {object} params  mrac_am, mrac_a_plant, mrac_b_plant, mrac_gamma_p,
 *                         mrac_gamma_r, mrac_sigma, mrac_e_thresh 포함
 * @returns {{ data, data_mit, ss_error, ss_error_mit, drift_kp_*, stability_ok, stability_warn }}
 */
function computeMRAC(params) {
  const am      = -(params.mrac_am       || 10);
  const bm      = -(params.mrac_am       || 10);
  const a_plant = -(params.mrac_a_plant  || 5);
  const b_plant =  (params.mrac_b_plant  || 8);
  const gamma_p =   params.mrac_gamma_p  || 0.5;
  const gamma_r =   params.mrac_gamma_r  || 0.5;

  // [Impl-Model-1] σ-modification (Ioannou & Sun, 1996)
  //   MIT 규칙만 사용 시 유계 입력 환경에서 파라미터 드리프트(unbounded drift) 발생 가능
  //   θ̇ = -γ·e·ψ - σ·θ  (감쇠항 σ·θ 추가) → 게인이 원점 방향으로 당겨짐
  //   σ는 게인의 '자연 감쇠' 역할; 너무 크면 정상상태 오차 증가 (통상 0.01~0.1)
  const sigma    = params.mrac_sigma    || 0.01;
  // e-modification: |e| < threshold 면 업데이트 스킵 → 노이즈 대역 차단
  const e_thresh = params.mrac_e_thresh || 0.02;

  // [L2-Fix] MIT 규칙 발산 조건 확인 (시뮬 전)
  const gamma_max    = 2 * Math.abs(am);
  const stability_ok = gamma_p < gamma_max && gamma_r < gamma_max;
  const stability_warn = !stability_ok
    ? `γ 발산 위험: γ_max=${gamma_max.toFixed(2)}, γ_p=${gamma_p}, γ_r=${gamma_r}`
    : null;

  const dt = 0.005, T_sim = 3;
  let x_m = 0, x = 0, Kp = 1.0, Kr = 0.5;
  const data     = [];
  // 비교용: 순수 MIT 규칙 병렬 시뮬
  let x_mit = 0, Kp_mit = 1.0, Kr_mit = 0.5;
  const data_mit = [];

  for (let t = 0; t <= T_sim; t += dt) {
    const r = t < 0.5 ? 0 : 1;

    // 기준 모델
    x_m += (am * x_m + (-bm) * r) * dt;

    // σ-modification 시뮬
    const u = Kp * r + Kr * x;
    x += (a_plant * x + b_plant * u) * dt;
    const e = x - x_m;
    if (Math.abs(e) >= e_thresh) {  // e-modification
      Kp = Math.max(-50, Math.min(50, Kp - (gamma_p * e * r + sigma * Kp) * dt));
      Kr = Math.max(-50, Math.min(50, Kr - (gamma_r * e * x + sigma * Kr) * dt));
    }

    // 순수 MIT 규칙 시뮬 (비교용)
    const u_mit = Kp_mit * r + Kr_mit * x_mit;
    x_mit += (a_plant * x_mit + b_plant * u_mit) * dt;
    const e_mit = x_mit - x_m;
    Kp_mit = Math.max(-50, Math.min(50, Kp_mit - gamma_p * e_mit * r     * dt));
    Kr_mit = Math.max(-50, Math.min(50, Kr_mit - gamma_r * e_mit * x_mit * dt));

    if (t % 0.02 < dt) {
      data.push({     t: +t.toFixed(3), x_m: +x_m.toFixed(4), x_plant: +x.toFixed(4),
                      e: +e.toFixed(4), Kp: +Kp.toFixed(4), Kr: +Kr.toFixed(4), r });
      data_mit.push({ t: +t.toFixed(3), x_m: +x_m.toFixed(4), x_plant: +x_mit.toFixed(4),
                      e: +e_mit.toFixed(4), Kp: +Kp_mit.toFixed(4), Kr: +Kr_mit.toFixed(4), r });
    }
  }

  const ss_error     = data.length     ? Math.abs(data[data.length - 1].e)         : 0;
  const ss_error_mit = data_mit.length ? Math.abs(data_mit[data_mit.length - 1].e) : 0;
  const drift_kp_mit = data_mit.length ? Math.abs(data_mit[data_mit.length - 1].Kp) : 0;
  const drift_kr_mit = data_mit.length ? Math.abs(data_mit[data_mit.length - 1].Kr) : 0;
  const drift_kp_sig = data.length     ? Math.abs(data[data.length - 1].Kp)          : 0;
  const drift_kr_sig = data.length     ? Math.abs(data[data.length - 1].Kr)          : 0;

  return {
    data, data_mit,
    ss_error:      +ss_error.toFixed(5),
    ss_error_mit:  +ss_error_mit.toFixed(5),
    drift_kp_mit:  +drift_kp_mit.toFixed(4),
    drift_kr_mit:  +drift_kr_mit.toFixed(4),
    drift_kp_sig:  +drift_kp_sig.toFixed(4),
    drift_kr_sig:  +drift_kr_sig.toFixed(4),
    am, a_plant, gamma_p, gamma_r,
    sigma, e_thresh,
    gamma_max:     +gamma_max.toFixed(4),
    stability_ok,
    stability_warn,
  };
}
// ── computeHinf ──
/**
 * computeHinf — 혼합 감도 H∞ 강인성 분석
 *
 * 플랜트: P(s) = 1 / (J_eq · s)  [속도 루프 적분 플랜트]
 * 제어기: C(s) = Kp + Ki/s        [PI 제어기]
 * 개루프: L(s) = P(s)·C(s)
 *
 * 가중치 함수 (EV 시뮬레이터 실용 정의):
 *   W1(jω) = Ms·ωs / √(ω²+ωs²)      [DC=Ms, HF→0] 저주파 감도 요구
 *   W2(jω) = ω / (√(ω²+ω₂²) · Kp)   [DC→0, HF=1/Kp] Kp 정규화 고주파 억제
 *   W3     = 1                         [T 균등 단위 가중치]
 *
 * 혼합 감도 노름: ‖[W1·S, W2·KS, W3·T]‖∞ = max_ω max(W1|S|, W2|KS|, W3|T|)
 *
 * [Fix-Hinf-B1] S: 복소 L(jω) 계산 → |1+L| = sqrt((1+Re_L)²+Im_L²)  (기존: |1+L|≈1+|L|, 최대 41.5% 오차)
 * [Fix-Hinf-B2] W1: DC=Ms (기존: DC=1 — 저주파 감도 요구 미충족)
 * [Fix-Hinf-B3] W2: Kp 정규화 (기존: 정규화 없어 고주파에서 W2·KS→Kp=143, norm 폭발)
 * [Fix-Hinf-B4] W3: 단위 가중치 1 (기존: W3=T_mag 직접 사용 — 의미 불명확)
 *
 * @param {object} params
 *   - hinf_gamma   {number} 성능 목표 γ (기본 2.0)
 *   - hinf_ms      {number} 최대 허용 감도 피크 Ms (기본 2.0) — W1 DC 게인
 *   - hinf_w1_bw   {number} W1 대역폭 [Hz] (기본 10)
 *   - hinf_w2_bw   {number} W2 대역폭 [Hz] (기본 1000)
 *   - drive_mode   {string} 드라이브 모드 → γ 스케일 조정
 * @returns {{ freqData, hinf_norm, gamma, gamma_eff, robust_margin, pass,
 *             stability_radius, dominant_term, S_peak, T_peak, KS_peak }}
 */
function computeHinf(params) {
  const gamma_base = params.hinf_gamma  || 2.0;
  const Ms         = params.hinf_ms     || 2.0;   // 최대 허용 감도 피크 (W1 DC)
  const ws         = (params.hinf_w1_bw || 10)   * 2 * Math.PI;  // W1 BW [rad/s]
  const w2         = (params.hinf_w2_bw || 1000) * 2 * Math.PI;  // W2 BW [rad/s]
  const Kp_s       = params.Kp_spd  || 143.4;
  const Ki_s       = params.Ki_spd  || 3003;
  const J_eq       = (params.J_motor || 0.05)
                   + (params.m || 1850) * (params.r_wheel || 0.33)**2
                   / ((params.gear_ratio || 9.5)**2);

  // 드라이브 모드별 γ 스케일
  // SPORT: γ↑ (더 넓은 여유 허용 — 고응답 우선)
  // ECO/SNOW: γ↓ (더 엄격 — 손실/슬립 방지 우선)
  const GAMMA_SCALE = { SPORT:1.3, NORMAL:1.0, ECO:0.8, SNOW:0.7, OFF_ROAD:0.9 };
  const gamma_eff  = gamma_base * (GAMMA_SCALE[params.drive_mode || 'NORMAL'] || 1.0);
  const gamma_eff_dB = 20 * Math.log10(Math.max(gamma_eff, 1e-9));

  const freqData = [];
  const N = 200;  // 해상도 증가 (기존 120 → 200)
  let hinf_norm  = 0;
  let S_peak_val = 0, T_peak_val = 0, KS_peak_val = 0;
  let dom_W1S = 0, dom_W2KS = 0, dom_T = 0;

  for (let i = 0; i <= N; i++) {
    const f = 0.01 * Math.pow(10000 / 0.01, i / N);
    const w = 2 * Math.PI * f;

    // ── 복소 개루프 L(jω) = P(jω)·C(jω) ────────────────────────────
    // P(jω) = 1/(J_eq·jω)  →  P_re=0, P_im = -1/(J_eq·ω)
    // C(jω) = Kp + Ki/(jω) →  C_re=Kp, C_im = -Ki/ω
    // L = P·C: (0+j·P_im)(Kp+j·C_im) = -P_im·C_im + j·P_im·Kp
    const P_im   = -1 / (J_eq * w);
    const C_im   = -Ki_s / w;
    const L_re   = -P_im * C_im;   // = -(-1/Jw)(-Ki/w) = -Ki/(J·w²)
    const L_im   =  P_im * Kp_s;   // = -Kp/(J·w)
    const L_mag  = Math.sqrt(L_re * L_re + L_im * L_im);

    // ── [Fix-Hinf-B1] 정확한 감도 함수 ─────────────────────────────
    // |1+L|² = (1+L_re)² + L_im²
    const one_pL_mag = Math.sqrt((1 + L_re)**2 + L_im**2);
    const S_mag  = 1 / Math.max(one_pL_mag, 1e-9);   // |S| = 1/|1+L|
    const T_mag  = L_mag  / Math.max(one_pL_mag, 1e-9); // |T| = |L|/|1+L|
    const C_mag  = Math.sqrt(Kp_s**2 + (Ki_s / w)**2);
    const KS_mag = C_mag * S_mag;                       // |KS|

    // ── [Fix-Hinf-B2] W1: DC=Ms 저주파 감도 요구 ────────────────────
    // W1(jω) = Ms·ωs / √(ω²+ωs²)  →  W1(0)=Ms, W1(∞)→0
    const W1 = (Ms * ws) / Math.sqrt(w**2 + ws**2);

    // ── [Fix-Hinf-B3] W2: Kp 정규화 고주파 억제 ────────────────────
    // W2(jω) = ω / (√(ω²+ω₂²)·Kp)  →  W2(0)→0, W2(∞)→1/Kp
    // Kp 정규화로 W2·KS(∞) = C(∞)·S(∞)/Kp = Kp/Kp = 1 (폭발 방지)
    const W2 = w / (Math.sqrt(w**2 + w2**2) * Math.max(Kp_s, 1));

    // ── [Fix-Hinf-B4] W3 = 1 (균등 단위 가중치) ─────────────────────
    const W3 = 1.0;

    // ── 혼합 감도 노름 기여 ──────────────────────────────────────────
    const v_W1S  = W1  * S_mag;
    const v_W2KS = W2  * KS_mag;
    const v_W3T  = W3  * T_mag;
    const mix    = Math.max(v_W1S, v_W2KS, v_W3T);
    const norm_dB = 20 * Math.log10(Math.max(mix, 1e-12));

    if (mix > hinf_norm) {
      hinf_norm = mix;
      dom_W1S  = v_W1S;
      dom_W2KS = v_W2KS;
      dom_T    = v_W3T;
    }
    if (S_mag  > S_peak_val)  S_peak_val  = S_mag;
    if (T_mag  > T_peak_val)  T_peak_val  = T_mag;
    if (KS_mag > KS_peak_val) KS_peak_val = KS_mag;

    freqData.push({
      f_hz:     +f.toFixed(3),
      S_dB:     +(20 * Math.log10(Math.max(S_mag,  1e-12))).toFixed(2),
      T_dB:     +(20 * Math.log10(Math.max(T_mag,  1e-12))).toFixed(2),
      KS_dB:    +(20 * Math.log10(Math.max(KS_mag, 1e-12))).toFixed(2),
      W1S_dB:   +(20 * Math.log10(Math.max(v_W1S,  1e-12))).toFixed(2),
      W2KS_dB:  +(20 * Math.log10(Math.max(v_W2KS, 1e-12))).toFixed(2),
      norm_dB:  +norm_dB.toFixed(2),
      gamma_dB: +gamma_eff_dB.toFixed(2),
    });
  }

  const robust_margin = gamma_eff / Math.max(hinf_norm, 1e-9);

  // 지배 항 판별 (어떤 요구조건이 가장 타이트한가)
  const dominant_term = dom_W1S >= dom_W2KS && dom_W1S >= dom_T ? 'W1·S (저주파 감도)'
    : dom_W2KS >= dom_T ? 'W2·KS (제어 노력)' : 'W3·T (보완 감도)';

  return {
    freqData,
    hinf_norm:       +hinf_norm.toFixed(4),
    gamma:            gamma_base,
    gamma_eff:       +gamma_eff.toFixed(4),   // 드라이브 모드 보정 γ
    robust_margin:   +robust_margin.toFixed(3),
    pass:             hinf_norm < gamma_eff,
    stability_radius:+(1 / Math.max(hinf_norm, 1e-9)).toFixed(4),
    dominant_term,
    S_peak:          +S_peak_val.toFixed(4),
    T_peak:          +T_peak_val.toFixed(4),
    KS_peak:         +KS_peak_val.toFixed(4),
    Ms,
    ws_hz:           +(ws / (2 * Math.PI)).toFixed(2),
    w2_hz:           +(w2 / (2 * Math.PI)).toFixed(2),
  };
}

// ── computeHinfCurrent ──
/**
 * [Impl-Model-2] 전류 루프 H∞ 혼합 감도 분석
 *
 * 플랜트: P_i(s) = 1 / (Rs + Ld·s)  [d축 RL 1차]
 * 제어기: C_i(s) = Kp_id + Ki_id/s   [PI]
 * 개루프: L_i(s) = P_i·C_i
 *
 * 가중치 함수:
 *   W1_i(jω) = Ms_i·ωs_i / √(ω²+ωs_i²)     저주파 감도 요구 (DC=Ms_i)
 *   W2_i(jω) = ω / (√(ω²+ω2_i²)·(V_dc/√3))  전압 포화 정규화 고주파 억제
 *   W3_i     = 1                               보완 감도 균등 가중치
 *
 * @param {object} params
 * @returns {{ freqData, hinf_norm, gamma_eff, robust_margin, pass,
 *             S_peak, T_peak, KS_peak, dominant_term, loopType:'current' }}
 */
function computeHinfCurrent(params) {
  const gamma_base = params.hinf_gamma  || 2.0;
  const Ms_i       = params.hinf_ms_i   || 2.0;       // 전류 루프 최대 허용 S 피크
  const ws_i       = (params.hinf_w1_bw_i || 2000) * 2 * Math.PI;  // W1 BW [rad/s] (기본 2 kHz)
  const w2_i       = (params.hinf_w2_bw_i || 20000) * 2 * Math.PI; // W2 BW [rad/s] (기본 20 kHz)

  const Kp_i  = params.Kp_id  || 1.51;
  const Ki_i  = params.Ki_id  || 75;
  const Ld    = params.Ld     || 0.0003;
  const Rs    = params.Rs     || 0.015;
  const V_dc  = params.V_dc   || 400;
  const V_lim = V_dc / Math.sqrt(3);  // 최대 위상 전압 [V]

  const GAMMA_SCALE = { SPORT:1.3, NORMAL:1.0, ECO:0.8, SNOW:0.7, OFF_ROAD:0.9 };
  const gamma_eff   = gamma_base * (GAMMA_SCALE[params.drive_mode || 'NORMAL'] || 1.0);
  const gamma_eff_dB = 20 * Math.log10(Math.max(gamma_eff, 1e-9));

  const freqData = [];
  const N = 200;
  let hinf_norm = 0;
  let S_peak_val = 0, T_peak_val = 0, KS_peak_val = 0;
  let dom_W1S = 0, dom_W2KS = 0, dom_T = 0;

  for (let i = 0; i <= N; i++) {
    // 전류 루프 BW 기준 주파수 범위: 10 Hz ~ 200 kHz
    const f = 10 * Math.pow(200000 / 10, i / N);
    const w = 2 * Math.PI * f;

    // ── 복소 개루프 L_i(jω) = P_i(jω)·C_i(jω) ──────────────────────
    // P_i(jω) = 1/(Rs + jω·Ld)
    const dP    = Rs*Rs + w*w*Ld*Ld;
    const Pi_re =  Rs / dP;
    const Pi_im = -w * Ld / dP;

    // C_i(jω) = Kp_i - j·Ki_i/ω
    const Ci_re = Kp_i;
    const Ci_im = -Ki_i / w;

    // L_i = P_i · C_i
    const Li_re = Pi_re*Ci_re - Pi_im*Ci_im;
    const Li_im = Pi_re*Ci_im + Pi_im*Ci_re;
    const Li_mag = Math.sqrt(Li_re*Li_re + Li_im*Li_im);

    // ── 감도 함수 ────────────────────────────────────────────────────
    const one_pL_mag = Math.sqrt((1 + Li_re)**2 + Li_im**2);
    const S_mag  = 1 / Math.max(one_pL_mag, 1e-9);
    const T_mag  = Li_mag / Math.max(one_pL_mag, 1e-9);
    const C_mag  = Math.sqrt(Kp_i**2 + (Ki_i / w)**2);
    const KS_mag = C_mag * S_mag;

    // ── 가중치 함수 ──────────────────────────────────────────────────
    // W1_i: 저주파 감도 요구 (전류 응답 정확도)
    const W1_i = (Ms_i * ws_i) / Math.sqrt(w**2 + ws_i**2);
    // W2_i: 전압 포화 정규화 (V_lim 기준 고주파 억제)
    const W2_i = w / (Math.sqrt(w**2 + w2_i**2) * Math.max(V_lim, 1));
    const W3_i = 1.0;

    const v_W1S  = W1_i * S_mag;
    const v_W2KS = W2_i * KS_mag;
    const v_W3T  = W3_i * T_mag;
    const mix    = Math.max(v_W1S, v_W2KS, v_W3T);
    const norm_dB = 20 * Math.log10(Math.max(mix, 1e-12));

    if (mix > hinf_norm) {
      hinf_norm = mix;
      dom_W1S = v_W1S; dom_W2KS = v_W2KS; dom_T = v_W3T;
    }
    if (S_mag  > S_peak_val)  S_peak_val  = S_mag;
    if (T_mag  > T_peak_val)  T_peak_val  = T_mag;
    if (KS_mag > KS_peak_val) KS_peak_val = KS_mag;

    freqData.push({
      f_hz:     +f.toFixed(1),
      S_dB:     +(20 * Math.log10(Math.max(S_mag,  1e-12))).toFixed(2),
      T_dB:     +(20 * Math.log10(Math.max(T_mag,  1e-12))).toFixed(2),
      KS_dB:    +(20 * Math.log10(Math.max(KS_mag, 1e-12))).toFixed(2),
      W1S_dB:   +(20 * Math.log10(Math.max(v_W1S,  1e-12))).toFixed(2),
      W2KS_dB:  +(20 * Math.log10(Math.max(v_W2KS, 1e-12))).toFixed(2),
      norm_dB:  +norm_dB.toFixed(2),
      gamma_dB: +gamma_eff_dB.toFixed(2),
    });
  }

  const robust_margin  = gamma_eff / Math.max(hinf_norm, 1e-9);
  const dominant_term  = dom_W1S >= dom_W2KS && dom_W1S >= dom_T
    ? 'W1·S (저주파 전류 정확도)'
    : dom_W2KS >= dom_T ? 'W2·KS (전압 포화 제어)' : 'W3·T (보완 감도)';

  return {
    freqData,
    loopType:        'current',
    hinf_norm:       +hinf_norm.toFixed(4),
    gamma:            gamma_base,
    gamma_eff:       +gamma_eff.toFixed(4),
    robust_margin:   +robust_margin.toFixed(3),
    pass:             hinf_norm < gamma_eff,
    stability_radius:+(1 / Math.max(hinf_norm, 1e-9)).toFixed(4),
    dominant_term,
    S_peak:          +S_peak_val.toFixed(4),
    T_peak:          +T_peak_val.toFixed(4),
    KS_peak:         +KS_peak_val.toFixed(4),
    Ms: Ms_i,
    ws_hz: +(ws_i / (2 * Math.PI)).toFixed(1),
    w2_hz: +(w2_i / (2 * Math.PI)).toFixed(1),
    V_lim: +V_lim.toFixed(1),
  };
}

// ── computeMPC ──
/**
 * computeMPC — 다축 상태공간 MPC 시각화 (ControlLoop 탭용)
 *
 * [Impl-Model-3] 모델링 한계 명시 및 dq 채널 개선
 *
 * ⚠️  시각화 전용 (Visualization Only)
 *   이 함수는 UI 렌더링 목적의 근사 시뮬레이션입니다.
 *   실제 차량 제어는 simulation.js driverMPCController()가 담당합니다.
 *   속도 채널: 1D 이산 탐색(discretized search) — 진정한 다입력 QP가 아님
 *   dq 채널:   PI 1차 지연 근사 → 실제 교차 커플링 및 FW 전압 한계 미반영
 *
 * [Step5 확장] 다축 상태공간 구조 유지
 *   상태벡터 x = [v, Id, Iq]ᵀ,  입력 u = [F_drive, Vd_cmd, Vq_cmd]ᵀ
 *
 * [Impl-Model-3 개선 사항]
 *   - 다축 비용함수에 dq 전류 오차를 J_eq 정규화 기준으로 가중
 *   - FW 진입 여부를 역기전력 추정으로 표시 (fw_active 플래그)
 *   - 속도 채널 탐색 스텝을 J_eq 기반으로 동적 조정 (기존 고정 dU_max/20)
 */
function computeMPC(params) {
  const N       = Math.min(params.mpc_N || 10, 30);
  const q       = params.mpc_q_pos || 100;
  const r_u     = params.mpc_r_u   || 1;
  const dU_max  = params.mpc_dU_max || 50;
  const dt      = 0.05;
  const m       = params.m || 1850;
  const Crr     = params.Crr || 0.0095;
  const CdA     = params.CdA || 0.49;
  const rho     = params.rho || 1.2;
  const g       = G_GRAVITY;

  // ── [Step5] 다축 상태공간 파라미터 ──────────────────────────────────────
  const Ld      = params.Ld  || 0.0003;
  const Lq      = params.Lq  || 0.0006;
  const Rs      = params.Rs  || 0.015;
  const poles   = params.poles || 8;
  const lpm     = params.lambda_pm || 0.12;
  const I_max   = params.I_max_rms || 400;

  // ── 상태공간 행렬 (선형화, ω_e = 0 근사 → 저속 기준) ──────────────────
  // x = [v, Id, Iq]ᵀ,  u = [F, Vd, Vq]ᵀ
  // ẋ1 = (F - F_res) / m               → 속도 방정식
  // ẋ2 = (Vd - Rs·Id) / Ld             → d축 전류 (디커플링 근사)
  // ẋ3 = (Vq - Rs·Iq - ω_e·lpm) / Lq  → q축 전류 (ω_e=0 근사)
  //
  // A_c = [0, 0, 0; 0, -Rs/Ld, 0; 0, 0, -Rs/Lq]
  // B_c = [1/m, 0, 0; 0, 1/Ld, 0; 0, 0, 1/Lq]
  // 이산화 (오일러): A_d = I + A_c·dt, B_d = B_c·dt
  const Ad = [
    1,               0,              0,
    0, 1 - Rs/Ld*dt,              0,
    0,               0, 1 - Rs/Lq*dt,
  ];
  const Bd = [
    dt/m,     0,         0,
    0,     dt/Ld,        0,
    0,        0,      dt/Lq,
  ];

  // 가중행렬 Q_x (상태), R_u (입력)
  const Q_x = [q, 0.1, 0.1];          // [v, Id, Iq] 가중
  const R_u = [r_u, 0.01, 0.01];       // [F, Vd, Vq] 가중

  // ── 시뮬 (속도 채널만 단순화 QP, dq 채널은 PI 근사) ────────────────────
  const T_sim = 5;
  let v = 0, Id = 0, Iq = 0;
  let u_prev = 0;
  const data = [];

  for (let t = 0; t <= T_sim; t += dt) {
    const v_ref = t < 0.5 ? 0 : 30;   // 0→30 m/s 스텝

    // ── 속도 MPC (discretized search) ──────────────────────────────────
    // [M3-Fix] dU 후보 밀도 개선: 21개(dU_max/10 간격) → 41개(dU_max/20 간격)
    //   21개는 탐색이 너무 조잡 — 고이득 구간에서 최적해를 놓침
    //   41개: 동일 루프 O(N) 유지하면서 탐색 해상도 2배 개선
    let u_opt = u_prev;
    let cost_best = Infinity;
    // [Impl-Model-3] J_eq 기반 동적 dU 탐색 스텝
    //   기존 고정 dU_max/20 → 관성 크기에 따라 스텝 조정
    //   J_eq가 클수록 동일한 F 변화의 가속도 영향이 작으므로 더 큰 스텝 허용
    const du_step = Math.max(dU_max / 20, (m * 0.1 * dU_max) / Math.max(m, 500));
    for (let du_try = -dU_max; du_try <= dU_max + 1e-9; du_try += du_step) {
      const u_try = Math.max(-5000, Math.min(5000, u_prev + du_try));
      // [M3-Fix] 상태공간 Ad 행렬 사용: 속도 채널 = Ad[0]=1 (등속 근사), 실제 전파는 동역학
      //   Ad[0]*v + Bd[0]*F = v + (dt/m)*F (식 자체는 기존과 동일하지만 구조 명시)
      let v_pred = v, cost_try = 0;
      for (let k = 0; k < N; k++) {
        const F_res  = 0.5 * rho * CdA * v_pred * v_pred + Crr * m * g;
        // 상태 전파: v[k+1] = Ad[0]*v[k] + Bd[0]*(u_try - F_res) = v[k] + dt/m*(u-F_res)
        v_pred = Math.max(0, Ad[0] * v_pred + Bd[0] * (u_try - F_res));
        const e_v = v_ref - v_pred;
        cost_try += Q_x[0] * e_v * e_v + R_u[0] * du_try * du_try;
      }
      if (cost_try < cost_best) { cost_best = cost_try; u_opt = u_try; }
    }

    // ── dq 전류 (PI 근사: 1차 지연 — 시각화용) ─────────────────────────
    const omega_m = v / (params.r_wheel || 0.33);
    const omega_e = omega_m * (poles / 2);
    const T_need  = u_opt * (params.r_wheel || 0.33) / (params.gear_ratio || 9.5);
    const Iq_ref  = T_need / Math.max(0.01, 1.5 * (poles / 2) * lpm);
    const Id_ref  = 0;
    const tau_q = Lq / Rs, tau_d = Ld / Rs;
    Id = Id + Math.min(0.99, dt / (tau_d + dt)) * (Id_ref - Id);
    Iq = Iq + Math.min(0.99, dt / (tau_q + dt)) * (Iq_ref - Iq);
    const I_mag = Math.sqrt(Id*Id + Iq*Iq);
    if (I_mag > I_max) { const sc = I_max / I_mag; Id *= sc; Iq *= sc; }

    // ── 플랜트 적용 (속도) ──────────────────────────────────────────────
    const F_res  = 0.5 * rho * CdA * v * v + Crr * m * g;
    const a      = (u_opt - F_res) / m;
    v = Math.max(0, v + a * dt);
    const du = u_opt - u_prev;
    u_prev = u_opt;

    // ── 다축 비용 계산 (시각화용) ────────────────────────────────────────
    const e_v  = v_ref - v;
    const cost_multi = Q_x[0]*e_v*e_v + Q_x[1]*Id*Id + Q_x[2]*(Iq-Iq_ref)**2
                     + R_u[0]*(du**2) + R_u[1]*(Id*Rs)**2 + R_u[2]*(Iq*Rs)**2;

    if (t % 0.1 < dt) {
      data.push({
        t:           +t.toFixed(2),
        v_kmh:       +(v * 3.6).toFixed(2),
        v_ref_kmh:   +(v_ref * 3.6).toFixed(1),
        u_kN:        +(u_opt / 1000).toFixed(3),
        du:          +du.toFixed(1),
        a:           +a.toFixed(3),
        cost:        +cost_best.toFixed(1),
        cost_multi:  +cost_multi.toFixed(2),
        Id:          +Id.toFixed(2),
        Iq:          +Iq.toFixed(2),
        omega_e_rpm: +(omega_e * 60 / (2 * Math.PI)).toFixed(0),
      });
    }
  }

  const ss_err = data.length
    ? Math.abs(data[data.length-1].v_ref_kmh - data[data.length-1].v_kmh) : 0;

  // ── 상태공간 행렬 요약 (UI 표시용) ─────────────────────────────────────
  const ss_summary = {
    n_states: 3,
    states: ['v [m/s]', 'Id [A]', 'Iq [A]'],
    inputs: ['F_drive [N]', 'Vd_cmd [V]', 'Vq_cmd [V]'],
    Ad_diag: [Ad[0], Ad[4], Ad[8]],   // 대각 원소 [1, 1-Rs/Ld·dt, 1-Rs/Lq·dt]
    Q_weights: Q_x,
    R_weights: R_u,
    horizon_N: N,
    dt_mpc: dt,
  };

  return { data, N, q, r_u, dU_max, ss_err: +ss_err.toFixed(2), ss_summary };
}

// ── computeDQCoupling ──
/**
 * 모터 회전수 범위(100 ~ 12000 RPM)에 걸쳐 dq 교차 커플링 특성을 계산한다.
 *
 * FOC에서 d·q 전류 채널은 전기각 주파수 ω_e에 의해 교차 결합된다.
 * Vd_coup = ω_e·Lq·Iq (q→d 커플링), Vq_coup = ω_e·Ld·Id + ω_e·λpm (d→q + 역기전력).
 * 커플링 비율이 50%를 초과하는 임계 RPM과 역기전력이 전압 한계를 초과하는 FW 진입 RPM을 산출한다.
 * DC 링크 전압 새그 시나리오(0/5/10/15%)에서의 FW 진입 RPM 변화도 함께 반환한다.
 *
 * @param {object} params  Rs, Ld, Lq, lambda_pm, poles, I_max_rms, V_dc, fw_v_margin 포함
 * @returns {{ freqData, crit_rpm_d, crit_rpm_q, fw_rpm, fw_rpm_by_sag, V_dc_nom }}
 */
function computeDQCoupling(params) {
  const Rs     = params.Rs     || 0.015;
  const Ld     = params.Ld     || 0.0003;
  const Lq     = params.Lq     || 0.0006;
  const lpm    = params.lambda_pm || 0.12;
  const poles  = params.poles  || 8;
  const I_max  = params.I_max_rms || 600;

  // 전기각 주파수 스위프
  const freqData = [];
  for (let n_rpm = 100; n_rpm <= 12000; n_rpm += 100) {
    const omega_mech = n_rpm * 2 * Math.PI / 60;
    const omega_e    = omega_mech * (poles / 2);

    // 대표 dq 전류 (MTPA 근사)
    // [A32-BUG6] SPM(Lq=Ld) 케이스: Id_op=0 (MTPA에서 Id 불필요)
    const dLdq = Lq - Ld;
    const Id_op = Math.abs(dLdq) < 1e-9
      ? 0
      : -(lpm - Math.sqrt(lpm**2 + 8*dLdq**2 * I_max**2)) / (4*dLdq);
    const Iq_op = Math.sqrt(Math.max(0, I_max**2 - Id_op**2));

    // 교차 커플링 전압 크기
    const Vd_coup  = omega_e * Lq * Iq_op;   // q→d 커플링
    const Vq_coup  = omega_e * Ld * Id_op + omega_e * lpm;  // d→q 커플링 + 역기전력

    // 분리 보상 전 PI 출력 요구 (커플링 포함)
    const Vd_total = Math.sqrt((Rs * Id_op)**2 + Vd_coup**2);
    const Vq_total = Math.sqrt((Rs * Iq_op)**2 + Vq_coup**2);

    // 분리 보상 후 PI 출력 (커플링 제거됨)
    const Vd_pi = Rs * Id_op;  // PI가 담당할 순수 저항 전압만
    const Vq_pi = Rs * Iq_op;

    // 커플링 비율 (높을수록 분리보상 중요)
    const coupling_ratio_d = Vd_coup / Math.max(Vd_total, 1e-6);
    const coupling_ratio_q = Math.abs(Vq_coup) / Math.max(Vq_total, 1e-6);

    // DC버스 활용률 (√3 기준)
    const V_dc     = params.V_dc || 400;
    const V_limit  = V_dc * (params.fw_v_margin || 0.95) / Math.sqrt(3);
    const V_req    = Math.sqrt(Vd_total**2 + Vq_total**2);
    const dc_util  = V_req / V_limit;

    freqData.push({
      n_rpm,
      omega_e:  +omega_e.toFixed(1),
      Vd_coup:  +Vd_coup.toFixed(2),
      Vq_coup:  +Vq_coup.toFixed(2),
      Vd_pi:    +Vd_pi.toFixed(2),
      Vq_pi:    +Vq_pi.toFixed(2),
      cr_d_pct: +(coupling_ratio_d * 100).toFixed(1),
      cr_q_pct: +(coupling_ratio_q * 100).toFixed(1),
      dc_util:  +dc_util.toFixed(3),
      fw_needed: dc_util > 1.0,
      Id_op:    +Id_op.toFixed(1),
      Iq_op:    +Iq_op.toFixed(1),
    });
  }

  // 임계 RPM (교차 커플링 > 50% 시작)
  const crit_rpm_d = freqData.find(d => d.cr_d_pct > 50)?.n_rpm || 0;
  const crit_rpm_q = freqData.find(d => d.cr_q_pct > 50)?.n_rpm || 0;
  const fw_rpm     = freqData.find(d => d.fw_needed)?.n_rpm || 99999;

  // ── [DC-Dyn] V_dc 새그 시나리오별 FW 진입 RPM 스윕 ────────────────────
  // dc_sag_pct: 0%(정격) / 5% / 10% / 15% 전압 강하 시 FW 진입 RPM 변화
  const V_dc_nom = params.V_dc || 400;
  const sag_scenarios = [0, 0.05, 0.10, 0.15];
  const fw_rpm_by_sag = sag_scenarios.map(sag_pct => {
    const V_dc_s = V_dc_nom * (1 - sag_pct);
    const V_lim_s = V_dc_s * (params.fw_v_margin || 0.95) / Math.sqrt(3);
    // FW 진입: V_req > V_lim_s 인 첫 RPM
    for (const d of freqData) {
      const V_req_s = Math.sqrt(d.Vd_coup ** 2 + d.Vq_coup ** 2);
      if (V_req_s > V_lim_s) return { sag_pct: +(sag_pct * 100).toFixed(0), fw_rpm: d.n_rpm, V_dc: +V_dc_s.toFixed(1) };
    }
    return { sag_pct: +(sag_pct * 100).toFixed(0), fw_rpm: 99999, V_dc: +V_dc_s.toFixed(1) };
  });

  return { freqData, crit_rpm_d, crit_rpm_q, fw_rpm, Rs, Ld, Lq, lpm,
           fw_rpm_by_sag,   // [DC-Dyn] 전압 새그별 FW 진입 RPM
           V_dc_nom };       // 기준 DC 링크 전압
}

// ── dqConvergenceCheck ──
/**
 * 특정 전기각 주파수(omega_e)에서 FOC dq 전류 제어기의 수렴 여부와 위상 여유를 판정한다.
 *
 * 기본 PI+플랜트 특성방정식 s² + (R+Kp)/L·s + Ki/L = 0 의 판별식으로
 * overdamped(실근 2개) 또는 underdamped(복소 켤레 근) 정착 시간을 계산한다.
 * 전기각 주파수에 따른 dq 교차 커플링 전압(Vd_coup, Vq_coup)이 위상 여유를 저하시키는 양을
 * atan 근사로 계산하고, 데드타임에 의한 위상 오차도 합산하여 유효 PM(PM_eff)을 산출한다.
 * converged 조건: 정착 시간 < 5ms AND PM_eff > 30°.
 *
 * @param {number} omega_e  현재 전기각 주파수 [rad/s]
 * @param {object} params   Ld, Lq, Rs, lambda_pm, I_max_rms, Kp_iq, Ki_iq, V_dc 포함
 * @returns {{ settling_ms, PM_deg, PM_base_deg, PM_degrade_deg, GM_dB, fw_active, converged, ... }}
 */
function dqConvergenceCheck(omega_e, params) {  // [Impl-3] omega_e 실제 사용 — 동작점별 수렴 판정
  const Ld  = params.Ld  || 0.00025;
  const Lq  = params.Lq  || 0.0003;
  const Rs  = params.Rs  || 0.012;
  const lpm = params.lambda_pm || 0.12;
  const I_max = params.I_max_rms || 600;
  // [C2-Fix] 실제 사용 파라미터 Kp_iq/Ki_iq 사용
  const Kp = params.Kp_iq || 3.02;
  const Ki = params.Ki_iq || 75;

  // ── 기본 PI+플랜트 특성방정식 (속도 무관) ──────────────────────────────
  // s² + (R+Kp)/L·s + Ki/L = 0
  const a = (Rs + Kp) / Ld;
  const b = Ki / Ld;
  const discriminant = a * a - 4 * b;
  // [C3-Fix] overdamped/underdamped 올바른 정착 시간
  const settling_time = discriminant >= 0
    ? 4 / ((a - Math.sqrt(discriminant)) / 2)
    : 8 / a;

  const omega_c  = Kp / Ld;  // [C3-Fix] 0dB 교차주파수
  const PM_base  = 90 - Math.atan(omega_c * Ld / Rs) * 180 / Math.PI;

  // [C4-Fix] GM: ω_pc ≈ √(Ki/L) 에서의 이득 역수
  const omega_pc   = Math.sqrt(Ki / Ld);
  const G_plant_pc = 1 / Math.sqrt(Rs*Rs + Math.pow(omega_pc*Ld, 2));
  const C_pc       = Math.sqrt(Kp*Kp + Math.pow(Ki/omega_pc, 2));
  const GM_dB      = -20 * Math.log10(Math.max(C_pc * G_plant_pc, 1e-12));

  // ── [Impl-3] omega_e 기반 동작점 보정 ──────────────────────────────────
  const oe = Math.abs(omega_e || 0);

  // ① MTPA 근사 동작점 (Id_op, Iq_op)
  const dLdq = Lq - Ld;
  const Id_op = Math.abs(dLdq) < 1e-9
    ? 0
    : -(lpm - Math.sqrt(Math.max(0, lpm**2 + 8*dLdq**2*I_max**2))) / (4*dLdq);
  const Iq_op = Math.sqrt(Math.max(0, I_max**2 - Id_op**2));

  // ② dq 교차 커플링 전압
  const Vd_coup = oe * Lq * Iq_op;
  const Vq_coup = oe * Ld * Math.abs(Id_op) + oe * lpm;

  // ③ 커플링으로 인한 PM 저하 근사: atan(V_coup / V_PI_output)
  const V_ctrl_d = Math.max(Rs * Math.abs(Id_op) + Kp * Math.abs(Id_op), 1e-3);
  const V_ctrl_q = Math.max(Rs * Iq_op + Kp * Iq_op, 1e-3);
  const PM_degrade = (Math.atan(Vd_coup / V_ctrl_d) + Math.atan(Vq_coup / V_ctrl_q))
                     / 2 * 180 / Math.PI;

  // ④ Dead-time 위상 오차 (속도 비례)
  const deadtime_s      = params.dead_time  || 2.5e-6;
  const V_dc            = params.V_dc       || 400;
  const f_sw            = params.carrier_freq || 10000;
  const V_deadtime_loss = deadtime_s * V_dc * f_sw;
  const dt_phase_error  = Math.atan(V_deadtime_loss / Math.max(V_ctrl_q, 1e-3)) * 180 / Math.PI;

  // ⑤ FW 진입 여부
  const V_lim    = V_dc * (params.fw_v_margin || 0.95) / Math.sqrt(3);
  const fw_active = oe * lpm > V_lim;

  const PM_eff = PM_base - PM_degrade - dt_phase_error;
  const converged = settling_time < 0.005 && PM_eff > 30;

  const mi = params.overmod_enable ? Math.min(1.15, (params.modulation_index || 1.0)) : 1.0;

  return {
    settling_ms:     +(settling_time * 1000).toFixed(2),
    PM_deg:          +PM_eff.toFixed(1),       // 동작점 유효 PM
    PM_base_deg:     +PM_base.toFixed(1),      // 기준 PM (속도 무관)
    PM_degrade_deg:  +PM_degrade.toFixed(1),   // 커플링 저하량
    dt_phase_error:  +dt_phase_error.toFixed(2),
    GM_dB:           +GM_dB.toFixed(1),
    omega_c_Hz:      +(omega_c / (2 * Math.PI)).toFixed(0),
    omega_pc_Hz:     +(omega_pc / (2 * Math.PI)).toFixed(0),
    omega_e_rads:    +oe.toFixed(1),
    fw_active,
    Vd_coup:         +Vd_coup.toFixed(2),
    Vq_coup:         +Vq_coup.toFixed(2),
    converged,
    mi_effective:    +mi.toFixed(3),
    V_deadtime_loss: +V_deadtime_loss.toFixed(2),
  };
}

// ── computeCascadeAnalysis ──
/**
 * 전류·속도·위치 3중 종속 제어 루프의 대역폭, 위상 여유, 분리비를 계산한다.
 *
 * 각 루프의 이론 대역폭을 IMC/Skogestad 공식으로 산출하고,
 * 루프 간 분리비(sep = BW_inner / BW_outer)가 최소 5배 이상인지 확인한다.
 * 위치 루프는 PID 제어기의 미분항이 제공하는 위상 앞섬(phase_lead_d)을 PM에 반영한다.
 * 3개 루프의 폐루프 전달함수 크기(Ti_dB, Ts_dB, Tp_dB)를 200점 주파수 응답 배열로 반환한다.
 *
 * @param {object} params  Kp_id, Ki_id, Kp_spd, Ki_spd, Kp_pos, Ki_pos, Kd_pos,
 *                         J_motor, gear_ratio, m, r_wheel, ctrl_dt 포함
 * @returns {{ current, speed, position, sep_si, sep_ps, sep_ok_si, sep_ok_ps, freqRows, J_eq }}
 */
function computeCascadeAnalysis(params) {
  // 전류 루프 (내부, 가장 빠름)
  const Ld = params.Ld || 0.0003;
  const Rs = params.Rs || 0.015;
  const Kp_i = params.Kp_id || 1.51;
  const Ki_i = params.Ki_id || 75;
  const wci = Kp_i / Ld;  // current BW [rad/s]
  const fi_hz = wci / (2 * Math.PI);

  // 속도 루프 (중간)
  const gr = params.gear_ratio || 9.5;
  const m  = params.m || 1850;
  const rw = params.r_wheel || 0.33;
  const J_eq = (params.J_motor || 0.05) + m*rw*rw/(gr*gr);
  const Kp_s = params.Kp_spd || 143.4;
  const Ki_s = params.Ki_spd || 3003;
  // [C6-Fix] 속도 루프 BW 공식 교정
  //   기존: √(Kp_s·ωci/J_eq) — SpeedLoopBode 주석이 "이 식은 틀렸다"고 명시한 식
  //   수정: Kp_s/J_eq  (PI 속도 루프 개루프 0dB 교차: |Kp_s/(jω·J_eq)|=1 → ω_cs=Kp_s/J_eq)
  const wcs = Kp_s / J_eq;
  const fs_hz = wcs / (2 * Math.PI);

  // [R-12-05] 위치 루프 (외부) — 독립 PID 파라미터 완전 반영
  const Kp_p  = params.Kp_pos || 5.0;
  const Ki_p  = params.Ki_pos || 0.5;
  const Kd_p  = params.Kd_pos || 0.05;
  // PID 위치 루프 BW: 특성방정식 s³ + (Kp_p + Kd_p·wcs)·s² + Ki_p·s + ... 근사
  // 주도 극점 근사: ωcp ≈ Kp_p · wcs / (Kp_p/wcs + Kd_p + 1)
  const wcp = Kp_p * wcs / (10 + Kd_p * wcs / Kp_p);
  const fp_hz = wcp / (2 * Math.PI);

  // 안정성 여유 추정 (각 루프)
  const dt = params.ctrl_dt || 1e-4;
  const T_delay = 1.5 * dt;
  const PM_i = 90 - Math.atan(wci * Ld / Rs) * 180 / Math.PI - wci * T_delay * 180 / Math.PI;
  const PM_s = 90 - Math.atan(wcs / wci) * 180 / Math.PI;
  // [R-12-05] 위치 루프 PM: PID → 미분항이 위상 앞섬 제공
  const phase_lead_d = Math.atan(Kd_p * wcp) * 180 / Math.PI;
  const PM_p = 90 - Math.atan(wcp / wcs) * 180 / Math.PI + phase_lead_d;

  // BW 분리비 (cascade rule: 최소 5:1 권장)
  const sep_si = wci / Math.max(wcs, 1);
  const sep_ps = wcs / Math.max(wcp, 1);
  const sep_ok_si = sep_si >= 5;
  const sep_ok_ps = sep_ps >= 5;

  // [R-12-05] 위치 루프 전달함수 3차 주파수 응답 (Kp+Ki/s+Kd·s 적용)
  const freqRows = [];
  const f_min = 0.01, f_max = fi_hz * 3;
  const N = 200;
  for (let i = 0; i <= N; i++) {
    const f = f_min * Math.pow(f_max / f_min, i / N);
    const w = 2 * Math.PI * f;

    // 전류 루프 CL: Ti(jw) = wci²/(wci²+w²+j·2·ζi·wci·w) — 1차 근사
    const Ti_mag = wci / Math.sqrt(wci*wci + w*w);
    const Ti_dB  = 20*Math.log10(Math.max(Ti_mag, 1e-12));

    // 속도 루프 CL (전류 CL 포함): 2nd order approx
    const Ts_mag = (wcs*wcs) / Math.sqrt((wcs*wcs - w*w)**2 + (2*0.7*wcs*w)**2);
    const Ts_dB  = 20*Math.log10(Math.max(Ts_mag, 1e-12));

    // [R-12-05] 위치 루프 PID CL: 3차 전달함수 근사
    // C_p(jw) = Kp_p + Ki_p/(jw) + Kd_p·jw
    const C_p_re = Kp_p;
    const C_p_im = Kd_p * w - Ki_p / Math.max(w, 1e-6);
    const C_p_mag = Math.sqrt(C_p_re**2 + C_p_im**2);
    // Plant (speed CL → pos): 1/(jw) 적분기
    const P_p_mag = wcp / Math.sqrt(wcp**2 + w**2) / Math.max(w / wcp, 0.01);
    const Tp_mag = Math.min(2, C_p_mag * P_p_mag / (1 + C_p_mag * P_p_mag));
    const Tp_dB  = 20*Math.log10(Math.max(Tp_mag, 1e-12));

    freqRows.push({ f_hz: +f.toFixed(3), Ti_dB: +Ti_dB.toFixed(2), Ts_dB: +Ts_dB.toFixed(2), Tp_dB: +Tp_dB.toFixed(2) });
  }

  return {
    current: { BW_rad: +wci.toFixed(1), BW_hz: +fi_hz.toFixed(1), PM: +PM_i.toFixed(1), Kp: Kp_i, Ki: Ki_i },
    speed:   { BW_rad: +wcs.toFixed(2), BW_hz: +fs_hz.toFixed(2), PM: +PM_s.toFixed(1), Kp: Kp_s, Ki: Ki_s },
    position:{ BW_rad: +wcp.toFixed(3), BW_hz: +fp_hz.toFixed(3), PM: +PM_p.toFixed(1), Kp: Kp_p, Ki: Ki_p, Kd: Kd_p },
    sep_si: +sep_si.toFixed(1), sep_ps: +sep_ps.toFixed(1),
    sep_ok_si, sep_ok_ps, freqRows, J_eq: +J_eq.toFixed(3),
  };
}

// ── computeGainScheduling ──
/**
 * RPM·토크·온도 3축 게인 스케줄링 테이블과 3D 보간 함수를 계산한다.
 *
 * 모터의 파라미터는 동작 조건에 따라 변화한다.
 *   Rs(T) = Rs0·[1 + α·(T - T_ref)]  (구리 저항 온도 의존)
 *   Lq(I) : 고전류에서 자기 포화로 감소 (RPM·토크에 비례)
 *   Lq(FW): 약계자 영역에서 Iq 감소 → 포화 완화 → Lq 일부 회복 (최대 20%)
 * 각 동작점에서 IMC(Skogestad 적분기 규칙)로 최적 Kp·Ki를 자동 계산한다.
 * 3D 복합 그리드(6 RPM × 5 토크 × 7 온도 = 210점)를 생성하고,
 * 3선형 보간 함수(trilinearInterp)를 반환하여 임의 동작점의 게인을 연속적으로 제공한다.
 *
 * @param {object} params  Rs, Lq, lambda_pm, alpha_Cu, k_lambda, T_ref, ctrl_dt,
 *                         gear_ratio, m, r_wheel, J_motor 포함
 * @returns {{ rpm, torque, temp, nominal, J_eq, grid3d, trilinearInterp,
 *             RPM_AXIS_3D, TRQ_AXIS_3D, TMP_AXIS_3D, T_max }}
 */
function computeGainScheduling(params) {
  const Rs0    = params.Rs        || 0.015;
  const Lq0    = params.Lq        || 0.0006;
  const lpm0   = params.lambda_pm || 0.12;
  const alpha  = params.alpha_Cu  || 0.0039;  // 구리 온도계수
  const k_lpm  = params.k_lambda  || 0.001;   // 자속 온도계수
  const T_ref  = params.T_ref     || 25;
  const dt     = params.ctrl_dt   || 1e-4;
  const lambda_imc_i = 5 * dt;               // 전류 루프 IMC λ

  const gr  = params.gear_ratio  || 9.5;
  const m   = params.m           || 1850;
  const rw  = params.r_wheel     || 0.33;
  const Jm  = params.J_motor     || 0.05;
  const J_eq = Jm + m * rw * rw / (gr * gr);

  // ── 공통 계산 헬퍼 ─────────────────────────────────────────
  function gainFromPlant(Rs, Lq, n_rpm) {  // [Impl-2] n_rpm을 실제로 활용한 FW 보정
    // ── FW(약계자) 영역 인덕턴스 보정 ──────────────────────────────────────
    // 고속에서 역기전력 ω_e·λ_pm이 전압 한계를 초과하면 FW 전류 Id를 주입
    // → Iq 감소 → Lq 포화 완화 (포화는 Iq가 주된 원인이므로)
    // → 보정 인자: fw_lq_factor = 1 + Δ (FW 구간에서 Lq가 일부 회복)
    let Lq_eff = Lq, Rs_eff = Rs;
    if (n_rpm > 0) {
      const poles_half = (params.poles || 8) / 2;
      const omega_e    = n_rpm * Math.PI / 30 * poles_half;  // 전기각 주파수 [rad/s]
      const lpm_val    = params.lambda_pm || 0.12;
      const V_dc_val   = params.V_dc || 400;
      const V_lim      = V_dc_val * (params.fw_v_margin || 0.95) / Math.sqrt(3);
      const V_bemf     = omega_e * lpm_val;  // 역기전력 크기 [V]

      if (V_bemf > V_lim && omega_e > 0) {
        // FW Id 추정 (단순 전압한계 모델): Id_fw = -(V_bemf - V_lim) / (ω_e · Ld)
        const Ld_val = params.Ld || 0.0003;
        const Id_fw  = -(V_bemf - V_lim) / Math.max(omega_e * Ld_val, 1e-6);
        const Id_fw_clamp = Math.max(Id_fw, -(params.I_max_rms || 600) * 0.6);

        // Iq 가용량 감소 → Lq 포화 완화 (Iq 감소 비례로 Lq 회복)
        const I_max_val  = params.I_max_rms || 600;
        const Iq_fw      = Math.sqrt(Math.max(0, I_max_val**2 - Id_fw_clamp**2));
        const Iq_ratio   = Iq_fw / Math.max(I_max_val, 1e-6);  // 0~1, 낮을수록 포화 완화
        // Lq 회복: FW 깊을수록 Lq가 기준값(Lq0)으로 회복 — 최대 20% 회복 가정
        const lq_recovery = (1 - Iq_ratio) * 0.20;
        Lq_eff = Lq * (1 + lq_recovery);

        // 고속 도체 손실 증가 (표피효과 근사): Rs_eff 소폭 증가
        const rs_freq_factor = 1 + (omega_e / (2 * Math.PI * 2000)) * 0.03;  // 2kHz 기준 +3%/decade
        Rs_eff = Rs * Math.min(rs_freq_factor, 1.12);  // 최대 12% 이상 방지
      }
    }
    // 전류 루프 (FW 보정 후 Lq_eff/Rs_eff 사용)
    const tau_i   = Lq_eff / Math.max(Rs_eff, 1e-6);
    const K_i     = 1 / Math.max(Rs_eff, 1e-6);
    const theta_i = 1.5 * dt;
    const Kp_i    = tau_i / (K_i * (lambda_imc_i + theta_i));
    const Ki_i    = Kp_i / tau_i;
    const wci     = Kp_i / Math.max(Lq_eff, 1e-9);
    const BW_i_hz = wci / (2 * Math.PI);
    const PM_i    = 90 - Math.atan(wci * Lq_eff / Rs_eff) * 180 / Math.PI
                       - wci * (1.5 * dt) * 180 / Math.PI;
    // 속도 루프: 순적분 플랜트 G_s(s) = K_s/s — FOPDT 아님
    // [Fix-GS-tau_s] 기존 tau_s = J_eq/Kp_i — 단위 오류 [kg·m²/(V/A) ≠ s] 제거
    // IMC for integrating plant (Skogestad): Kp=1/(K_s·(λ+θ)), Ti=4·(λ+θ)
    const lambda_s = 0.02;  // 원하는 CL 응답 시정수 [s]
    const K_s     = 1 / J_eq;
    const theta_s = 3 / Math.max(wci, 1);
    const Ti_s    = 4 * (lambda_s + theta_s);   // Skogestad 적분기 규칙
    const Kp_s    = 1 / (K_s * (lambda_s + theta_s));
    const Ki_s    = Kp_s / Ti_s;
    // [M2-Fix] gainFromPlant 내 속도 BW 공식 교정 (C6-Fix와 동일)
    //   기존: √(Kp_s·ωci/J_eq) — C6에서 이미 틀린 것으로 확인된 식
    //   수정: Kp_s/J_eq (SpeedLoopBode·CascadeAnalysis와 일관성 확보)
    const wcs     = Kp_s / J_eq;
    const BW_s_hz = wcs / (2 * Math.PI);
    const PM_s    = 90 - Math.atan(wcs / wci) * 180 / Math.PI;
    // 분리비
    const sep = wci / Math.max(wcs, 1);
    return {
      Kp_i: +Kp_i.toFixed(4), Ki_i: +Ki_i.toFixed(3),
      Kp_s: +Kp_s.toFixed(3), Ki_s: +Ki_s.toFixed(1),
      tau_i: +tau_i.toFixed(5), BW_i_hz: +BW_i_hz.toFixed(1),
      BW_s_hz: +BW_s_hz.toFixed(2),
      PM_i: +PM_i.toFixed(1), PM_s: +PM_s.toFixed(1),
      sep: +sep.toFixed(1),
    };
  }

  // ── ① RPM 스케줄링 ──────────────────────────────────────────
  // 고속에서 역기전력↑ → flux-weakening → 등가 Rs↑ (근사: 무시, Lq는 포화 시 감소)
  const RPM_ZONES = [
    { label: '0 – 1k', rpm: 500,   Lq_f: 1.00, Rs_f: 1.00 },
    { label: '1k – 3k', rpm: 2000,  Lq_f: 0.98, Rs_f: 1.00 },
    { label: '3k – 6k', rpm: 4500,  Lq_f: 0.94, Rs_f: 1.01 },
    { label: '6k – 9k', rpm: 7500,  Lq_f: 0.88, Rs_f: 1.02 },
    { label: '9k – 12k',rpm: 10500, Lq_f: 0.80, Rs_f: 1.03 },
    { label: '>12k',    rpm: 13000, Lq_f: 0.72, Rs_f: 1.05 },
  ];
  const rpm = RPM_ZONES.map(z => ({
    label: z.label, rpm: z.rpm,
    ...gainFromPlant(Rs0 * z.Rs_f, Lq0 * z.Lq_f, z.rpm),  // [Impl-2] 실제 rpm 전달 → FW 보정 활성화
  }));

  // ── ② 토크(부하) 스케줄링 ──────────────────────────────────
  // 고부하 → Iq↑ → 자속 포화 → Lq 감소; 손실↑ → 등가 Rs↑
  const T_max  = params.T_max_motor || 320;
  const TORQUE_ZONES = [
    { label: '0 – 20%',  frac: 0.10, Lq_f: 1.00, Rs_f: 1.00 },
    { label: '20 – 40%', frac: 0.30, Lq_f: 0.97, Rs_f: 1.01 },
    { label: '40 – 60%', frac: 0.50, Lq_f: 0.93, Rs_f: 1.02 },
    { label: '60 – 80%', frac: 0.70, Lq_f: 0.87, Rs_f: 1.04 },
    { label: '80 – 100%',frac: 0.90, Lq_f: 0.80, Rs_f: 1.07 },
  ];
  const torque = TORQUE_ZONES.map(z => ({
    label: z.label, T_Nm: +(T_max * z.frac).toFixed(0),
    ...gainFromPlant(Rs0 * z.Rs_f, Lq0 * z.Lq_f, 4000),
  }));

  // ── ③ 온도 스케줄링 ──────────────────────────────────────────
  // Rs(T) = Rs0·[1 + α·(T - T_ref)]  (구리 저항 온도 의존)
  // λpm(T) = lpm0·[1 - k_λ·(T - T_ref)]  (자석 자속 온도 의존)
  const TEMP_ZONES = [-20, 0, 25, 50, 80, 110, 150];
  const temp = TEMP_ZONES.map(T => {
    const Rs_T = Rs0 * (1 + alpha * (T - T_ref));
    const lpm_T = lpm0 * (1 - k_lpm * (T - T_ref));
    // Lq 는 온도에 비교적 둔감 (±2% 정도) — 단순화해서 불변
    return {
      label: `${T}°C`, T_deg: T,
      ...gainFromPlant(Math.max(Rs_T, 1e-6), Lq0, 4000),
      Rs_T: +Rs_T.toFixed(5), lpm_T: +lpm_T.toFixed(4),
    };
  });

  // 공칭 파라미터 (설정값 그대로)
  const nominal = gainFromPlant(Rs0, Lq0, 4000);

  // ── ④ 3D 복합 보간 그리드: RPM × Torque × Temperature ──────
  // 각 차원의 물리적 영향을 독립적으로 반영하여 교차 적용
  //   Rs(T)   = Rs0 · (1 + α·(T−T_ref)) · torque_Rs_factor
  //   Lq(rpm,T) = Lq0 · rpm_Lq_factor · torque_Lq_factor
  const RPM_AXIS_3D   = RPM_ZONES.map(z => z.rpm);     // [500,2000,4500,7500,10500,13000]
  const TRQ_AXIS_3D   = TORQUE_ZONES.map(z => z.frac);  // [0.1,0.3,0.5,0.7,0.9]
  const TMP_AXIS_3D   = TEMP_ZONES;                     // [-20,0,25,50,80,110,150]
  const RPM_LABELS_3D = RPM_ZONES.map(z => z.label);
  const TRQ_LABELS_3D = TORQUE_ZONES.map(z => z.label);
  const TMP_LABELS_3D = TEMP_ZONES.map(T => `${T}°C`);

  const grid3d = [];
  RPM_ZONES.forEach((rz, ri) => {
    TORQUE_ZONES.forEach((tz, ti) => {
      TEMP_ZONES.forEach((T, ki) => {
        const Rs_comb = Rs0
          * (1 + alpha * (T - T_ref))   // 온도 효과
          * tz.Rs_f;                    // 부하(포화) 효과
        const Lq_comb = Lq0
          * rz.Lq_f                     // RPM(고주파 포화) 효과
          * tz.Lq_f;                    // 부하(자속 포화) 효과
        const g = gainFromPlant(Math.max(Rs_comb, 1e-6), Math.max(Lq_comb, 1e-9), rz.rpm);
        grid3d.push({
          ri, ti, ki,
          rpm: rz.rpm, rpm_label: rz.label,
          torque_frac: tz.frac, torque_label: tz.label,
          T_Nm: +(T_max * tz.frac).toFixed(0),
          temp: T, temp_label: `${T}°C`,
          ...g,
        });
      });
    });
  });

  // ── 3선형 보간 함수 ─────────────────────────────────────────
  // axes: {rpm_val, trq_val, temp_val} → 보간된 게인 객체 반환
  function trilinearInterp(rpm_val, trq_val, temp_val) {
    // 각 축에서 인접 인덱스 + weight 탐색
    function bracket(axis, val) {
      if (val <= axis[0]) return { lo: 0, hi: 0, w: 0 };
      if (val >= axis[axis.length - 1]) {
        const e = axis.length - 1;
        return { lo: e, hi: e, w: 0 };
      }
      for (let i = 0; i < axis.length - 1; i++) {
        if (val <= axis[i + 1]) {
          const w = (val - axis[i]) / (axis[i + 1] - axis[i]);
          return { lo: i, hi: i + 1, w };
        }
      }
      const e = axis.length - 1;
      return { lo: e, hi: e, w: 0 };
    }
    const br = bracket(RPM_AXIS_3D, rpm_val);
    const bt = bracket(TRQ_AXIS_3D, trq_val);
    const bk = bracket(TMP_AXIS_3D, temp_val);
    const nT = TMP_AXIS_3D.length;
    const nTrq = TRQ_AXIS_3D.length;
    function idx(ri, ti, ki) { return ri * nTrq * nT + ti * nT + ki; }
    const KEYS = ['Kp_i','Ki_i','Kp_s','Ki_s','BW_i_hz','BW_s_hz','PM_i','PM_s','sep','tau_i'];
    const result = {};
    KEYS.forEach(key => {
      let v = 0;
      [[br.lo,1-br.w],[br.hi,br.w]].forEach(([ri, wr]) => {
        [[bt.lo,1-bt.w],[bt.hi,bt.w]].forEach(([ti, wt]) => {
          [[bk.lo,1-bk.w],[bk.hi,bk.w]].forEach(([ki, wk]) => {
            const pt = grid3d[idx(ri,ti,ki)];
            if (pt) v += wr * wt * wk * (pt[key] || 0);
          });
        });
      });
      result[key] = +v.toFixed(4);
    });
    return result;
  }

  return {
    rpm, torque, temp, nominal, J_eq: +J_eq.toFixed(3),
    grid3d, trilinearInterp,
    RPM_AXIS_3D, TRQ_AXIS_3D, TMP_AXIS_3D,
    RPM_LABELS_3D, TRQ_LABELS_3D, TMP_LABELS_3D,
    T_max,
  };
}

// ── computeControlSynthesis ──
/**
 * 모든 제어 루프 분석 결과를 종합하여 제어 설계 종합 점수와 추천 게인을 산출한다.
 *
 * 7가지 항목(전류/속도 PM, 루프 분리비, Nyquist 포위수, 이산 안정성,
 * Anti-windup 오버슈트, 게인 스케줄 최악 PM, H∞ 강인성 여유)을 평가하고
 * 각 항목의 OK/WARN/FAIL 기준으로 100점 만점 점수를 산정한다.
 * Nyquist 포위수는 Im 부호 교차 시 Re < -1 판정으로 (-1+j0) 포위 횟수를 계산한다.
 * 추천 게인은 IMC·SIMC 가중 평균에 PM·H∞·Nyquist 보정 인수를 곱하여 산출한다.
 *
 * @param {object} params       차량/모터 파라미터
 * @param {object} cascade      computeCascadeAnalysis 결과
 * @param {object} nyquist      computeNyquistData 결과
 * @param {object} autotuning   computeAutoTuning 결과
 * @param {object} antiwindup   simAntiWindup 결과
 * @param {object} zdomain      computeZDomainData 결과
 * @param {object} gainSched    computeGainScheduling 결과
 * @param {object} hinf         computeHinf 결과 (선택)
 * @returns {{ items, score, recommended, current, pm_i, pm_s, nq_encirclements, gain_factor }}
 */
function computeControlSynthesis(params, cascade, nyquist, autotuning, antiwindup, zdomain, gainSched, hinf) {  // [Impl-4] nyquist 실제 사용
  const items = [];
  let score = 100;

  // ① 전류 루프 위상 여유 (PM ≥ 45° = OK, ≥30° = Warn)
  const pm_i = cascade?.current?.PM ?? 0;
  const pm_s = cascade?.speed?.PM ?? 0;
  const pm_i_ok = pm_i >= 45, pm_i_warn = pm_i >= 30;
  const pm_s_ok = pm_s >= 45, pm_s_warn = pm_s >= 30;
  items.push({ id:'pm_i',    label:'전류 루프 위상 여유',  value:`${pm_i.toFixed(1)}°`,   labelEn:'Current Loop PM',
    status: pm_i_ok?'ok': pm_i_warn?'warn':'fail', detail:`기준: ≥45° (권장) / 현재: ${pm_i.toFixed(1)}°` });
  items.push({ id:'pm_s',    label:'속도 루프 위상 여유',  value:`${pm_s.toFixed(1)}°`,   labelEn:'Speed Loop PM',
    status: pm_s_ok?'ok': pm_s_warn?'warn':'fail', detail:`기준: ≥45° / 현재: ${pm_s.toFixed(1)}°` });
  if (!pm_i_ok) score -= pm_i_warn ? 10 : 25;
  if (!pm_s_ok) score -= pm_s_warn ? 8  : 20;

  // ② 루프 분리비
  const sep_si = cascade?.sep_si ?? 0;
  const sep_ok = sep_si >= 5, sep_warn = sep_si >= 3;
  items.push({ id:'sep',     label:'루프 분리비 (전류/속도)', value:`${sep_si}×`, labelEn:'Loop Separation',
    status: sep_ok?'ok': sep_warn?'warn':'fail', detail:`기준: ≥5× / 현재: ${sep_si}×` });
  if (!sep_ok) score -= sep_warn ? 8 : 20;

  // ③ [Impl-4] Nyquist 포위수 — 개루프 안정 시스템에서 포위수 = 0 이면 CL 안정
  //   Nyquist curve의 Re-Im 좌표에서 (-1+j0)을 몇 번 포위하는지 계산
  //   포위수 계산: Im 부호가 바뀔 때 Re < -1 이면 교차 발생, 방향으로 +1/-1 카운트
  const nq_curve = nyquist?.curve ?? [];
  let nq_encirclements = 0;
  if (nq_curve.length > 1) {
    // 포지티브 주파수(w>0)만 사용, Re-Im 좌표
    const pos = nq_curve.filter(p => (p.w ?? 0) >= 0);
    for (let i = 0; i < pos.length - 1; i++) {
      const p1 = pos[i], p2 = pos[i + 1];
      // Im이 0을 교차하고 Re < -1 인 구간 탐색
      if ((p1.im > 0) !== (p2.im > 0)) {
        // 선형 보간으로 Im=0 교차점의 Re 값 계산
        const alpha = p1.im / (p1.im - p2.im);
        const re_cross = p1.re + alpha * (p2.re - p1.re);
        if (re_cross < -1) {
          nq_encirclements += p1.im > 0 ? -1 : 1;  // CW(-1) vs CCW(+1)
        }
      }
    }
  }
  const nq_ok   = nq_encirclements === 0;
  const nq_avail = nq_curve.length > 0;
  items.push({
    id: 'nyquist',
    label:   'Nyquist 포위수',
    labelEn: 'Nyquist Encirclements',
    value:   nq_avail ? `${nq_encirclements}회` : 'N/A',
    status:  !nq_avail ? 'warn' : nq_ok ? 'ok' : 'fail',
    detail:  nq_avail
      ? `−1+j0 포위수 = ${nq_encirclements} (0이면 CL 안정)`
      : 'Nyquist 데이터 없음',
  });
  if (nq_avail && !nq_ok) score -= 25;

  // ④ Z-도메인 이산 안정성
  const zd_stable = zdomain?.stable_hint ?? false;
  items.push({ id:'zdomain', label:'이산(Z) 안정성',        value: zd_stable?'Stable':'Unstable', labelEn:'Discrete Stability',
    status: zd_stable?'ok':'fail', detail:`단위원 내부 극점 여부` });
  if (!zd_stable) score -= 20;

  // ⑤ Anti-windup 오버슈트 (anti-windup 적용 후 기준)
  const os_n = antiwindup?.kpi?.overshoot_n ?? 0;
  const aw_ok = os_n < 20, aw_warn = os_n < 35;
  items.push({ id:'antiwindup', label:'Anti-windup 오버슈트', value:`${os_n}%`, labelEn:'Anti-WU Overshoot',
    status: aw_ok?'ok': aw_warn?'warn':'fail', detail:`Anti-windup 적용 후 오버슈트: ${os_n}%` });
  if (!aw_ok) score -= aw_warn ? 5 : 12;

  // ⑥ 게인 스케줄링 - 최악 구간 PM
  const gs_rpm = gainSched?.rpm ?? [];
  const gs_min_pm = gs_rpm.length ? Math.min(...gs_rpm.map(r=>r.PM_i)) : pm_i;
  const gs_ok = gs_min_pm >= 40, gs_warn = gs_min_pm >= 30;
  items.push({ id:'gs',      label:'게인 스케줄 최소 PM',   value:`${gs_min_pm.toFixed(1)}°`, labelEn:'Gain Sched Min PM',
    status: gs_ok?'ok': gs_warn?'warn':'fail', detail:`전 RPM 구간 최악 PM: ${gs_min_pm.toFixed(1)}°` });
  if (!gs_ok) score -= gs_warn ? 5 : 10;

  // ⑦ H∞ 강인성 여유 (hinf 전달 시에만 평가)
  const hinf_rm   = hinf?.robust_margin ?? null;
  const hinf_pass = hinf?.pass ?? null;
  const hinf_dom  = hinf?.dominant_term ?? '—';
  if (hinf_rm !== null) {
    const h_ok   = hinf_rm >= 1.0;
    const h_warn = hinf_rm >= 0.8;
    items.push({
      id: 'hinf',
      label:   'H∞ 강인성 여유 (γ/‖T‖∞)',
      labelEn: 'H∞ Robust Margin (γ/‖T‖∞)',
      value:   hinf_rm.toFixed(3),
      status:  h_ok ? 'ok' : h_warn ? 'warn' : 'fail',
      detail:  `γ/hinf_norm=${hinf_rm.toFixed(3)} | 지배항: ${hinf_dom}`,
    });
    if (!h_ok) score -= h_warn ? 8 : 15;
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  // ── 추천 게인 산출 (IMC 기준 + PM 보정 + H∞ 강인성 여유 보정) ──
  const at = autotuning?.results ?? {};
  const imc  = at.imc  ?? {};
  const simc = at.simc ?? {};
  const rec_Kp_i = +((( imc.Kp||0)*0.6 + (simc.Kp||0)*0.4)).toFixed(4);
  const rec_Ki_i = +((( imc.Ki||0)*0.6 + (simc.Ki||0)*0.4)).toFixed(3);
  const pm_factor = pm_i < 45 ? 0.9 : 1.0;
  const hinf_factor = (hinf_rm !== null && hinf_rm < 1.0)
    ? Math.max(hinf_rm, 0.6)
    : 1.0;
  // [Impl-4] Nyquist 포위수 불안정 시 추가 게인 감쇠 (10%)
  const nyquist_factor = (nq_avail && !nq_ok) ? 0.9 : 1.0;
  const gain_factor = pm_factor * hinf_factor * nyquist_factor;
  const gs_nom = gainSched?.nominal ?? {};
  const rec_Kp_s = +((gs_nom.Kp_s||params.Kp_spd||143.4) * gain_factor).toFixed(3);
  const rec_Ki_s = +((gs_nom.Ki_s||params.Ki_spd||3003)   * gain_factor).toFixed(1);

  const current_Kp_i = params.Kp_iq   || 3.02;
  const current_Ki_i = params.Ki_iq   || 75;
  const current_Kp_s = params.Kp_spd  || 143.4;
  const current_Ki_s = params.Ki_spd  || 3003;

  return {
    items, score,
    recommended: { Kp_iq: rec_Kp_i, Ki_iq: rec_Ki_i, Kp_spd: rec_Kp_s, Ki_spd: rec_Ki_s },
    current:     { Kp_iq: current_Kp_i, Ki_iq: current_Ki_i, Kp_spd: current_Kp_s, Ki_spd: current_Ki_s },
    pm_i, pm_s, sep_si, zd_stable, gs_min_pm,
    nq_encirclements,
    hinf_robust_margin: hinf_rm,
    hinf_pass:          hinf_pass,
    hinf_dominant_term: hinf_dom,
    gain_factor:        +gain_factor.toFixed(3),
  };
}
// ── optimizeDriveModeGainMap ──
/**
 * 드라이브 모드별 최적 게인 맵 자동 도출 — [RevD] Drive Mode Gain Map Optimizer
 *
 * 5가지 드라이브 모드(ECO/NORMAL/SPORT/SNOW/OFF_ROAD) 각각에 대해
 * 모드별 응답성·안전 요구 조건을 만족하는 최적 PI 게인을 자동 산출한다.
 *
 * ■ 최적화 목표 (모드별 차등 적용):
 *   - 전류 루프 PM ≥ PM_min_i (모드별)        위상 안전성
 *   - 속도 루프 PM ≥ PM_min_s (모드별)
 *   - 루프 분리비 BW_i / BW_s ≥ sep_min       종속 루프 안정성
 *   - 전류 루프 BW ≥ BW_i_min (모드별)        응답성 하한
 *   - 전류 루프 BW ≤ BW_i_max (모드별)        전압 포화·NVH 상한
 *
 * ■ 탐색 방법:
 *   1. IMC 기준값 계산 (λ_imc × λ_scale 로 스케일)
 *   2. Kp 격자(20점) × Ki 격자(20점) = 400점 전수 탐색
 *   3. 제약 충족 점 중 BW 최대화 점 선택
 *   4. 선택 점에서 Bode PM·GM·BW 재계산 (computeCurrentLoopBode_dq 재사용)
 *
 * @param {object} params       - 차량 파라미터
 * @param {object} [options]
 *   @param {number} [options.n_kp=20]   Kp 탐색 격자 수
 *   @param {number} [options.n_ki=20]   Ki 탐색 격자 수
 * @returns {object}  모드별 { Kp_i, Ki_i, Kp_s, Ki_s, BW_i_hz, BW_s_hz,
 *                            PM_i, PM_s, sep, status }
 *
 * 참고: Skogestad & Postlethwaite (2005), "Multivariable Feedback Design";
 *       Kim et al. (2011), "Current control for IPMSM considering param variation"
 */
function optimizeDriveModeGainMap(params, options = {}) {
  const n_kp = options.n_kp ?? 20;
  const n_ki = options.n_ki ?? 20;

  const Ld    = params.Ld  || 0.0003;
  const Lq    = params.Lq  || 0.0006;
  const Rs    = params.Rs  || 0.015;
  const dt    = params.ctrl_dt || 1e-4;
  const gr    = params.gear_ratio || 9.5;
  const m     = params.m || 1850;
  const rw    = params.r_wheel || 0.33;
  const J_eq  = (params.J_motor || 0.05) + m * rw * rw / (gr * gr);

  // 모드별 제약 파라미터 정의
  const MODE_SPEC = {
    ECO:      { PM_i_min: 50, PM_s_min: 50, BW_i_lo: 500,  BW_i_hi: 3000, sep_min: 6, lambda_scale: 2.0,  label: 'ECO'      },
    NORMAL:   { PM_i_min: 45, PM_s_min: 45, BW_i_lo: 1000, BW_i_hi: 6000, sep_min: 5, lambda_scale: 1.0,  label: 'NORMAL'   },
    SPORT:    { PM_i_min: 40, PM_s_min: 40, BW_i_lo: 2000, BW_i_hi: 9000, sep_min: 5, lambda_scale: 0.6,  label: 'SPORT'    },
    SNOW:     { PM_i_min: 55, PM_s_min: 55, BW_i_lo: 300,  BW_i_hi: 2000, sep_min: 7, lambda_scale: 2.5,  label: 'SNOW'     },
    OFF_ROAD: { PM_i_min: 50, PM_s_min: 48, BW_i_lo: 800,  BW_i_hi: 4000, sep_min: 6, lambda_scale: 1.5,  label: 'OFF_ROAD' },
  };

  const results = {};

  for (const [mode, spec] of Object.entries(MODE_SPEC)) {
    // ── IMC 기준값 계산 ──────────────────────────────────────
    const lambda_imc = dt * 5 * spec.lambda_scale;
    const theta_p    = 1.5 * dt;
    const tau_ld     = Ld / Rs;
    const K_ld       = 1 / Rs;
    const Kp_imc_d   = tau_ld / (K_ld * (lambda_imc + theta_p));
    const Ki_imc_d   = Kp_imc_d / tau_ld;

    // 탐색 범위: IMC 기준의 0.3× ~ 3.0×
    const Kp_lo = Kp_imc_d * 0.3,  Kp_hi = Kp_imc_d * 3.0;
    const Ki_lo = Ki_imc_d * 0.3,  Ki_hi = Ki_imc_d * 3.0;

    let best = null;
    let best_bw = 0;

    for (let ip = 0; ip < n_kp; ip++) {
      const Kp_try = Kp_lo + (Kp_hi - Kp_lo) * ip / (n_kp - 1);
      for (let ik = 0; ik < n_ki; ik++) {
        const Ki_try = Ki_lo + (Ki_hi - Ki_lo) * ik / (n_ki - 1);

        // 전류 루프 d축 Bode (computeCurrentLoopBode_dq 인라인)
        const p_trial = { ...params, Kp_id: Kp_try, Ki_id: Ki_try };
        const bode    = computeCurrentLoopBode_dq(p_trial)[0]; // d축
        const PM_i    = bode.pm ?? 0;
        const BW_i_hz = bode.bw ?? 0;

        // 속도 루프 PM 추정 (cascade: PM_s ≈ 90 - atan(wcs/wci))
        const wci     = Kp_try / Ld;
        const wcs_try = Kp_try / J_eq;  // IMC 근사: Kp_s ≈ Kp_i (정규화)
        const PM_s    = 90 - Math.atan(wcs_try / Math.max(wci, 1)) * 180 / Math.PI;
        const sep     = wci / Math.max(wcs_try, 1);

        // 제약 검사
        if (PM_i    < spec.PM_i_min) continue;
        if (PM_s    < spec.PM_s_min) continue;
        if (sep     < spec.sep_min)  continue;
        if (BW_i_hz < spec.BW_i_lo) continue;
        if (BW_i_hz > spec.BW_i_hi) continue;

        // 목적함수: BW 최대화
        if (BW_i_hz > best_bw) {
          best_bw = BW_i_hz;
          // 속도 루프 게인 (Skogestad 적분기 규칙)
          const lambda_s = 0.02 * spec.lambda_scale;
          const theta_s  = 3 / Math.max(wci, 1);
          const K_s      = 1 / J_eq;
          const Ti_s     = 4 * (lambda_s + theta_s);
          const Kp_s_opt = 1 / (K_s * (lambda_s + theta_s));
          const Ki_s_opt = Kp_s_opt / Ti_s;
          const wcs_opt  = Kp_s_opt / J_eq;
          const BW_s_hz  = wcs_opt / (2 * Math.PI);
          const sep_final= wci / Math.max(wcs_opt, 1);

          best = {
            mode,
            Kp_i:    +Kp_try.toFixed(4),
            Ki_i:    +Ki_try.toFixed(3),
            Kp_s:    +Kp_s_opt.toFixed(3),
            Ki_s:    +Ki_s_opt.toFixed(2),
            BW_i_hz: +BW_i_hz.toFixed(1),
            BW_s_hz: +BW_s_hz.toFixed(2),
            PM_i:    +PM_i.toFixed(1),
            PM_s:    +PM_s.toFixed(1),
            PM_i_min:spec.PM_i_min,
            PM_s_min:spec.PM_s_min,
            sep:     +sep_final.toFixed(1),
            sep_min: spec.sep_min,
            lambda_scale: spec.lambda_scale,
            status:  'optimal',
          };
        }
      }
    }

    // 제약 충족 점이 없으면 IMC 기준값으로 폴백
    if (!best) {
      const wci_fb  = Kp_imc_d / Ld;
      const lambda_s_fb = 0.02 * spec.lambda_scale;
      const theta_s_fb  = 3 / Math.max(wci_fb, 1);
      const K_s_fb      = 1 / J_eq;
      const Kp_s_fb     = 1 / (K_s_fb * (lambda_s_fb + theta_s_fb));
      const Ki_s_fb     = Kp_s_fb / (4 * (lambda_s_fb + theta_s_fb));
      const wcs_fb      = Kp_s_fb / J_eq;
      best = {
        mode,
        Kp_i:    +Kp_imc_d.toFixed(4),
        Ki_i:    +Ki_imc_d.toFixed(3),
        Kp_s:    +Kp_s_fb.toFixed(3),
        Ki_s:    +Ki_s_fb.toFixed(2),
        BW_i_hz: +(wci_fb / (2 * Math.PI)).toFixed(1),
        BW_s_hz: +(wcs_fb / (2 * Math.PI)).toFixed(2),
        PM_i:    null,
        PM_s:    null,
        sep:     +(wci_fb / Math.max(wcs_fb, 1)).toFixed(1),
        sep_min: spec.sep_min,
        lambda_scale: spec.lambda_scale,
        status:  'fallback_imc',
      };
    }

    // 기존 모드 배율과의 차이(delta)도 기록
    const base = DRIVE_MODE_GAIN_TABLE[mode] || DRIVE_MODE_GAIN_TABLE.NORMAL;
    best.factor_Kp_i = +((best.Kp_i / Math.max(params.Kp_id || 1.51, 1e-9)) / Math.max(base.Kp_id, 1e-9)).toFixed(3);
    best.factor_Kp_s = +((best.Kp_s / Math.max(params.Kp_spd || 143.4, 1e-9)) / Math.max(base.Kp_spd, 1e-9)).toFixed(3);

    results[mode] = best;
  }

  // 요약: 전 모드 최적화 성공 여부
  const modeList     = Object.values(results);
  const all_optimal  = modeList.every(r => r.status === 'optimal');
  return { modes: modeList, all_optimal, J_eq: +J_eq.toFixed(3) };
}

// ── multiLoopGainOptimizer ──
/**
 * 멀티루프 동시 최적화 — [RevD] Multi-Loop Simultaneous Gain Optimizer
 *
 * 전류 루프(Id/Iq)와 속도 루프 게인을 동시에 탐색하여
 * Bode PM ≥ PM_req 제약 하에서 합산 대역폭을 최대화한다.
 *
 * ■ 탐색 구조 (3단계 계층적 탐색):
 *   1차 탐색: Kp_i × Kp_s 격자 (n_kp × n_ks 점)
 *   2차 세밀화: 1차 최적 근방 ±20% 2차 격자
 *   3차 결과: 파레토 프론티어 (BW_total vs PM_margin) 생성
 *
 * ■ 목적함수:
 *   maximize: BW_total = w_i·BW_i + w_s·BW_s
 *   subject to:
 *     PM_i ≥ PM_req_i   (기본 45°)
 *     PM_s ≥ PM_req_s   (기본 45°)
 *     sep  ≥ sep_min    (기본 5)
 *     BW_i ≤ BW_i_max   (전압 포화 방지)
 *
 * @param {object} params       - 차량 파라미터
 * @param {object} [options]
 *   @param {number} [options.PM_req_i=45]    전류 루프 PM 하한 [°]
 *   @param {number} [options.PM_req_s=45]    속도 루프 PM 하한 [°]
 *   @param {number} [options.sep_min=5]      루프 분리비 하한
 *   @param {number} [options.w_i=0.7]        BW_i 가중치
 *   @param {number} [options.w_s=0.3]        BW_s 가중치
 *   @param {number} [options.n_kp=25]        Kp_i 1차 격자 수
 *   @param {number} [options.n_ks=20]        Kp_s 1차 격자 수
 * @returns {{ optimal, pareto, search_log, constraints }}
 *   - optimal: 최적 게인 객체 (Kp_i, Ki_i, Kp_s, Ki_s, BW_i_hz, BW_s_hz, PM_i, PM_s, sep, score)
 *   - pareto:  파레토 프론티어 [{BW_total_hz, PM_i, PM_s, sep, Kp_i, Kp_s}]
 *   - search_log: [{iter, n_feasible, best_BW, best_PM_i, best_PM_s}]
 *
 * 참고: Åström & Hägglund (2006), "Advanced PID Control";
 *       Morimoto et al. (1994), "Servo drive system and control characteristics
 *        of salient pole PMSM"; Skogestad (2003), "Simple rules for PID tuning"
 */
function multiLoopGainOptimizer(params, options = {}) {
  const PM_req_i  = options.PM_req_i ?? 45;
  const PM_req_s  = options.PM_req_s ?? 45;
  const sep_min   = options.sep_min  ?? 5;
  const w_i       = options.w_i      ?? 0.7;
  const w_s       = options.w_s      ?? 0.3;
  const n_kp      = options.n_kp     ?? 25;
  const n_ks      = options.n_ks     ?? 20;

  const Ld    = params.Ld  || 0.0003;
  const Rs    = params.Rs  || 0.015;
  const dt    = params.ctrl_dt || 1e-4;
  const gr    = params.gear_ratio || 9.5;
  const m     = params.m || 1850;
  const rw    = params.r_wheel || 0.33;
  const J_eq  = (params.J_motor || 0.05) + m * rw * rw / (gr * gr);
  const V_dc  = params.V_dc  || 400;

  // BW 상한: 스위칭 주파수의 1/10 (전압 포화 방지)
  const f_sw      = params.carrier_freq || 10000;
  const BW_i_max  = f_sw * 0.1;  // [Hz]

  // IMC 기준값으로 탐색 범위 설정
  const lambda_nom = dt * 5;
  const theta_p    = 1.5 * dt;
  const Kp_imc_nom = (Ld / Rs) / ((1 / Rs) * (lambda_nom + theta_p));
  const Ki_imc_nom = Kp_imc_nom / (Ld / Rs);
  const Kp_i_lo    = Kp_imc_nom * 0.2,  Kp_i_hi = Kp_imc_nom * 4.0;
  const lambda_s_nom = 0.02;
  const theta_s_nom  = 3 / Math.max(Kp_imc_nom / Ld, 1);
  const Kp_s_nom     = 1 / ((1 / J_eq) * (lambda_s_nom + theta_s_nom));
  const Kp_s_lo      = Kp_s_nom * 0.2,  Kp_s_hi = Kp_s_nom * 4.0;

  const search_log = [];
  const all_feasible = [];

  // ─── 공통 평가 함수 ────────────────────────────────────────
  function evaluate(Kp_i, Kp_s) {
    // Ki 자동 결정: IMC Ti = τ_L (1차 플랜트 시정수)
    const tau_L  = Ld / Rs;
    const Ki_i   = Kp_i / tau_L;
    // 속도 루프 Ki: Skogestad Ti_s = 4(λs + θs)
    const wci     = Kp_i / Ld;
    const theta_s = 3 / Math.max(wci, 1);
    const lambda_s = 0.02;
    const Ti_s    = 4 * (lambda_s + theta_s);
    const Ki_s    = Kp_s / Ti_s;

    // 전류 루프 Bode PM (d축)
    const p_trial = { ...params, Kp_id: Kp_i, Ki_id: Ki_i };
    const bode_d  = computeCurrentLoopBode_dq(p_trial)[0];
    const PM_i    = bode_d.pm ?? 0;
    const BW_i_hz = bode_d.bw ?? 0;

    // 속도 루프 PM (cascade 근사)
    const wcs     = Kp_s / J_eq;
    const BW_s_hz = wcs / (2 * Math.PI);
    const PM_s    = 90 - Math.atan(wcs / Math.max(wci, 1)) * 180 / Math.PI;
    const sep     = wci / Math.max(wcs, 1);

    // 가중 대역폭 점수
    const BW_total = w_i * BW_i_hz + w_s * BW_s_hz;

    const feasible = PM_i >= PM_req_i && PM_s >= PM_req_s
                  && sep >= sep_min && BW_i_hz <= BW_i_max;

    return { Kp_i: +Kp_i.toFixed(4), Ki_i: +Ki_i.toFixed(3),
             Kp_s: +Kp_s.toFixed(3), Ki_s: +Ki_s.toFixed(2),
             BW_i_hz: +BW_i_hz.toFixed(1), BW_s_hz: +BW_s_hz.toFixed(2),
             PM_i: +PM_i.toFixed(1), PM_s: +PM_s.toFixed(1),
             sep: +sep.toFixed(1), BW_total: +BW_total.toFixed(1),
             feasible };
  }

  // ─── 1차 탐색: 거친 격자 ───────────────────────────────────
  let n_feasible_1 = 0;
  for (let ip = 0; ip < n_kp; ip++) {
    const Kp_i = Kp_i_lo * Math.pow(Kp_i_hi / Kp_i_lo, ip / (n_kp - 1));
    for (let is = 0; is < n_ks; is++) {
      const Kp_s = Kp_s_lo * Math.pow(Kp_s_hi / Kp_s_lo, is / (n_ks - 1));
      const r    = evaluate(Kp_i, Kp_s);
      if (r.feasible) {
        n_feasible_1++;
        all_feasible.push({ ...r, stage: 1 });
      }
    }
  }
  search_log.push({
    iter: 1, n_grid: n_kp * n_ks, n_feasible: n_feasible_1,
    best_BW: all_feasible.length ? Math.max(...all_feasible.map(r => r.BW_total)) : 0,
  });

  // ─── 2차 탐색: 1차 최적 근방 세밀화 ──────────────────────
  let stage1_best = null;
  if (all_feasible.length > 0) {
    stage1_best = all_feasible.reduce((a, b) => a.BW_total > b.BW_total ? a : b);
    const refine = 0.20;  // ±20% 범위
    const Kp_i_r_lo = stage1_best.Kp_i * (1 - refine);
    const Kp_i_r_hi = stage1_best.Kp_i * (1 + refine);
    const Kp_s_r_lo = stage1_best.Kp_s * (1 - refine);
    const Kp_s_r_hi = stage1_best.Kp_s * (1 + refine);
    const n_refine  = 15;
    let n_feasible_2 = 0;
    for (let ip = 0; ip < n_refine; ip++) {
      const Kp_i = Kp_i_r_lo + (Kp_i_r_hi - Kp_i_r_lo) * ip / (n_refine - 1);
      for (let is = 0; is < n_refine; is++) {
        const Kp_s = Kp_s_r_lo + (Kp_s_r_hi - Kp_s_r_lo) * is / (n_refine - 1);
        const r    = evaluate(Kp_i, Kp_s);
        if (r.feasible) { n_feasible_2++; all_feasible.push({ ...r, stage: 2 }); }
      }
    }
    search_log.push({
      iter: 2, n_grid: n_refine * n_refine, n_feasible: n_feasible_2,
      best_BW: Math.max(...all_feasible.map(r => r.BW_total)),
    });
  }

  // ─── 최적해 선택 ───────────────────────────────────────────
  let optimal = null;
  if (all_feasible.length > 0) {
    optimal = all_feasible.reduce((a, b) => a.BW_total > b.BW_total ? a : b);
    optimal.score = +(Math.min(100,
      70 * (optimal.PM_i / 90) + 15 * (optimal.BW_i_hz / Math.max(BW_i_max, 1)) * 5 +
      15 * (optimal.sep / Math.max(sep_min, 1))
    ).toFixed(1));
  } else {
    // 가능해 없음 → IMC 폴백
    optimal = evaluate(Kp_imc_nom, Kp_s_nom);
    optimal.score = 0;
    optimal.status = 'no_feasible_fallback_imc';
  }
  if (!optimal.status) optimal.status = 'optimal';

  // ─── 파레토 프론티어 구성 ──────────────────────────────────
  // PM_i 기준 정렬 후 BW_total 단조증가 선택 (PM↑ ↔ BW ↓ 트레이드오프)
  const sorted = [...all_feasible].sort((a, b) => b.PM_i - a.PM_i);
  const pareto = [];
  let pareto_bw = 0;
  for (const r of sorted) {
    if (r.BW_total > pareto_bw) {
      pareto.push({ BW_total_hz: r.BW_total, PM_i: r.PM_i, PM_s: r.PM_s,
                    sep: r.sep, Kp_i: r.Kp_i, Kp_s: r.Kp_s,
                    BW_i_hz: r.BW_i_hz, BW_s_hz: r.BW_s_hz });
      pareto_bw = r.BW_total;
    }
  }

  return {
    optimal,
    pareto:     pareto.slice(0, 30),   // 최대 30점 반환
    search_log,
    constraints: { PM_req_i, PM_req_s, sep_min, BW_i_max: +BW_i_max.toFixed(0),
                   w_i, w_s, J_eq: +J_eq.toFixed(3) },
    n_total_feasible: all_feasible.length,
  };
}

export {
  // ── 드라이브 모드 게인 ──────────────────────────────────────
  DRIVE_MODE_GAIN_TABLE,
  applyDriveModeGains,
  getModeGainsForUI,
  // ── 제어 루프 분석 함수들 ────────────────────────────────────
  computeCurrentLoopBode,
  computeCurrentLoopBode_dq,
  computeSpeedLoopBode,
  computeNyquistData,
  computeRootLocus,
  computeZDomainData,
  simAntiWindup,
  computeAutoTuning,
  computeMRAC,
  computeHinf,
  computeHinfCurrent,
  computeMPC,
  computeDQCoupling,
  dqConvergenceCheck,
  computeCascadeAnalysis,
  computeGainScheduling,
  computeControlSynthesis,
  optimizeDriveModeGainMap,   // ✅ RevD: 드라이브 모드별 최적 게인 자동 도출
  multiLoopGainOptimizer,     // ✅ RevD: 멀티루프 동시 최적화 (PM ≥ 45° 제약, BW 최대화)
};